export const TREND_STRUCTURE_TIMEFRAMES = ['5', '15', '30', '60', '240', '720', 'D', 'W', 'M'];

import { OPERATIONAL_CONTRACTS } from './operational-contract-registry.mjs';
// Cripto Alert: estrutura de extremos (um HH/HL/LH/LL por tendência). Só lê o estado do motor.
import {
  advanceCaStructure,
  buildCaStructure,
  CA_STRUCTURE_RULE,
  caStructurePoints,
  cloneCaStructureState,
} from './ca-structure.mjs';

export { CA_STRUCTURE_RULE, caStructurePoints };

export const TREND_STRUCTURE_MIN_READY_CANDLES = 5;
export const TREND_STRUCTURE_INCREMENTAL_ENGINE_VERSION =
  OPERATIONAL_CONTRACTS.trendStructure.engineVersion;
export const TREND_STRUCTURE_PRIMARY_RANGE_STATE_VERSION =
  OPERATIONAL_CONTRACTS.trendStructure.primaryRangeStateVersion;
export const TREND_STRUCTURE_INCREMENTAL_CANDLE_LIMIT = 120;
export const TREND_STRUCTURE_PIVOT_HISTORY_LIMIT = 6;

const HIGH_PIVOT_TYPES = new Set(['H', 'HH', 'LH']);
const LOW_PIVOT_TYPES = new Set(['L', 'HL', 'LL']);

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toTimestamp(value) {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const time = Date.parse(value);

  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

export function normalizeTrendStructureCandles(candles) {
  return candles
    .map((candle) => {
      const startAt = toTimestamp(candle.startAt ?? candle.start_at);
      const endAt = toTimestamp(candle.endAt ?? candle.end_at);
      const open = toNumber(candle.open ?? candle.open_price);
      const high = toNumber(candle.high ?? candle.high_price);
      const low = toNumber(candle.low ?? candle.low_price);
      const close = toNumber(candle.close ?? candle.close_price);

      if (
        !startAt ||
        !endAt ||
        open === null ||
        high === null ||
        low === null ||
        close === null ||
        high < low
      ) {
        return null;
      }

      return {
        close,
        endAt,
        high,
        low,
        open,
        startAt,
      };
    })
    .filter(Boolean)
    .sort((left, right) => Date.parse(left.startAt) - Date.parse(right.startAt));
}

function compactTrendStructureCandle(candle) {
  return [candle.startAt, candle.endAt, candle.open, candle.high, candle.low, candle.close];
}

function expandTrendStructureCandle(value) {
  if (Array.isArray(value)) {
    const [startAt, endAt, open, high, low, close] = value;

    return {
      close,
      endAt,
      high,
      low,
      open,
      startAt,
    };
  }

  if (value && typeof value === 'object') {
    return value;
  }

  return null;
}

export function buildTrendStructureIncrementalMemory(
  inputCandles,
  limit = TREND_STRUCTURE_INCREMENTAL_CANDLE_LIMIT,
) {
  const normalized = normalizeTrendStructureCandles(inputCandles);
  const safeLimit =
    Number.isInteger(limit) && limit >= TREND_STRUCTURE_MIN_READY_CANDLES
      ? limit
      : TREND_STRUCTURE_INCREMENTAL_CANDLE_LIMIT;
  const candles = normalized.slice(-safeLimit);
  const lastCandle = candles.at(-1) ?? null;

  return {
    candles: candles.map(compactTrendStructureCandle),
    candleCount: candles.length,
    lastCandleAt: lastCandle?.endAt ?? null,
    limit: safeLimit,
    version: TREND_STRUCTURE_INCREMENTAL_ENGINE_VERSION,
  };
}

export function restoreTrendStructureIncrementalCandles(metadata) {
  const incremental = metadata?.incremental;

  if (!incremental || incremental.version !== TREND_STRUCTURE_INCREMENTAL_ENGINE_VERSION) {
    return [];
  }

  const rawCandles = Array.isArray(incremental.candles) ? incremental.candles : [];

  return normalizeTrendStructureCandles(rawCandles.map(expandTrendStructureCandle).filter(Boolean));
}

function snapshotLastCandleAt(snapshot) {
  return toTimestamp(snapshot?.last_candle_at ?? snapshot?.lastCandleAt);
}

function cloneCharacterChange(change) {
  if (!change) {
    return null;
  }

  const cloned = {
    ...change,
    referencePivot: cloneFlowPoint(change.referencePivot),
  };

  if (change.candle) {
    cloned.candle = {
      at: change.candle.at,
      close: roundPrice(change.candle.close),
      high: roundPrice(change.candle.high),
      low: roundPrice(change.candle.low),
    };
  } else {
    delete cloned.candle;
  }

  return cloned;
}

function clonePrimaryRangeSweep(sweep) {
  return sweep
    ? {
        at: sweep.at,
        direction: sweep.direction,
        high: roundPrice(sweep.high),
        low: roundPrice(sweep.low),
        referencePivot: cloneFlowPoint(sweep.referencePivot),
      }
    : null;
}

function primaryRangeSweepFromCandle(direction, candle, referencePivot) {
  return {
    at: candle.startAt,
    direction,
    high: roundPrice(candle.high),
    low: roundPrice(candle.low),
    referencePivot: cloneFlowPoint(referencePivot),
  };
}

function isCanonicalPrimaryRange(range) {
  return (
    range?.stateVersion === TREND_STRUCTURE_PRIMARY_RANGE_STATE_VERSION &&
    range?.trend
  );
}

function canonicalTrendStateFromPrimaryRange(range) {
  if (!isCanonicalPrimaryRange(range)) {
    return null;
  }

  return {
    activeSwing: cloneFlowPoint(range.activeSwing ?? range.activePivot),
    characterChange: cloneCharacterChange(range.characterChange),
    confirmedHH: cloneFlowPoint(range.confirmedHH ?? (range.trend === 'bullish' ? range.top : null)),
    confirmedHL: cloneFlowPoint(range.confirmedHL ?? (range.trend === 'bullish' ? range.bottom : null)),
    confirmedLH: cloneFlowPoint(range.confirmedLH ?? (range.trend === 'bearish' ? range.top : null)),
    confirmedLL: cloneFlowPoint(range.confirmedLL ?? (range.trend === 'bearish' ? range.bottom : null)),
    lastOfficialPivot: cloneFlowPoint(range.lastOfficialPivot ?? range.lastPivot),
    lastProcessedCandleAt: range.lastProcessedCandleAt ?? null,
    finalizationLevel: toNumber(range.finalizationLevel),
    finalizationRule: typeof range.finalizationRule === 'string' ? range.finalizationRule : null,
    localFlow: {
      activePivot: cloneFlowPoint(range.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(range.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(range.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(range.localFlow?.internalTop),
    },
    mode:
      range.mode ??
      (range.movementAxis === 'seeking_top'
        ? range.trend === 'bearish'
          ? 'bearish_tracking_provisional_lh'
          : 'bullish_tracking_hh'
        : range.movementAxis === 'seeking_bottom'
          ? range.trend === 'bearish'
            ? 'bearish_tracking_ll'
            : 'bullish_tracking_provisional_hl'
          : range.state === 'choch_active'
            ? range.trend === 'bearish'
              ? 'choch_up_active'
              : 'choch_down_active'
            : 'neutral_bootstrap'),
    neutralHigh: range.trend === 'neutral' ? cloneFlowPoint(range.top) : null,
    neutralLow: range.trend === 'neutral' ? cloneFlowPoint(range.bottom) : null,
    pivotHistory: normalizePivotHistory(range.pivotHistory),
    recordPivotHistory: true,
    reversal: range.reversal
      ? {
          bottom: cloneFlowPoint(range.reversal.bottom),
          low: cloneFlowPoint(range.reversal.low),
          stage: range.reversal.stage ?? null,
          top: cloneFlowPoint(range.reversal.top),
      }
      : null,
    preconfirmedAt: typeof range.preconfirmedAt === 'string' ? range.preconfirmedAt : null,
    preconfirmedPivot: cloneFlowPoint(range.preconfirmedPivot),
    preconfirmedReference: cloneFlowPoint(range.preconfirmedReference),
    sweep: clonePrimaryRangeSweep(range.sweep),
    trend: range.trend,
  };
}

export function advanceTrendStructureFromSnapshot(
  previousSnapshot,
  nextCandle,
  options = {},
) {
  const [normalizedNext] = normalizeTrendStructureCandles([nextCandle]);
  const previousLastCandleAt = snapshotLastCandleAt(previousSnapshot);

  if (!normalizedNext) {
    return {
      needsRebuild: true,
      reason: 'invalid_next_candle',
    };
  }

  if (!previousSnapshot || previousSnapshot.state !== 'ready' || !previousLastCandleAt) {
    return {
      needsRebuild: true,
      reason: 'missing_ready_snapshot',
    };
  }

  if (Date.parse(previousLastCandleAt) !== Date.parse(normalizedNext.startAt)) {
    return {
      needsRebuild: true,
      reason: 'candle_gap',
    };
  }

  const memoryCandles = restoreTrendStructureIncrementalCandles(previousSnapshot.metadata ?? {});
  const memoryLast = memoryCandles.at(-1) ?? null;

  if (!memoryLast) {
    return {
      needsRebuild: true,
      reason: 'missing_incremental_memory',
    };
  }

  if (Date.parse(memoryLast.endAt) !== Date.parse(previousLastCandleAt)) {
    return {
      needsRebuild: true,
      reason: 'stale_incremental_memory',
    };
  }

  const limit = options.limit ?? TREND_STRUCTURE_INCREMENTAL_CANDLE_LIMIT;
  const sourceCandles = [...memoryCandles, normalizedNext].slice(-limit);
  const storedPrimaryRange = previousSnapshot.metadata?.flow?.primaryRange;

  if (!isCanonicalPrimaryRange(storedPrimaryRange)) {
    return {
      needsRebuild: true,
      reason: 'legacy_primary_range',
    };
  }

  const state = candidateOfficialStateFromPrimaryRange(storedPrimaryRange);

  if (!state) {
    return {
      needsRebuild: true,
      reason: 'legacy_primary_range',
    };
  }

  const events = [];
  candidateOfficialAdvanceState(state, memoryLast, normalizedNext, events);
  // Sem estado guardado (snapshot antigo), a estrutura recomeça a partir da memória de candles.
  const caStructure = advanceCaStructure(
    cloneCaStructureState(previousSnapshot.metadata?.flow?.caStructureState),
    candidateOfficialDeriveTrend(state),
    sourceCandles,
    sourceCandles.length - 1,
  );

  return {
    events,
    needsRebuild: false,
    reason: null,
    snapshot: snapshotFromCandidateOfficialTrendState(sourceCandles.length, normalizedNext.endAt, state, caStructure),
    sourceCandles,
  };
}

function roundPrice(value) {
  return value === null || value === undefined || !Number.isFinite(value)
    ? null
    : Number(value.toFixed(12));
}

function buildEmptySnapshot(candleCount, state, lastCandleAt = null) {
  return {
    candidatePivotPrice: null,
    candidatePivotType: null,
    candidateStartedAt: null,
    candleCount,
    hhAt: null,
    hhPrice: null,
    hlAt: null,
    hlPrice: null,
    lastCandleAt,
    lastPivotAt: null,
    lastPivotPrice: null,
    lastPivotType: null,
    lhAt: null,
    lhPrice: null,
    llAt: null,
    llPrice: null,
    motherCandleHigh: null,
    motherCandleLow: null,
    motherCandleStartAt: null,
    phase: 'range',
    provisionalPivotAt: null,
    provisionalPivotConfirmation: null,
    provisionalPivotPrice: null,
    provisionalPivotType: null,
    state,
    trend: 'neutral',
    validHighAt: null,
    validHighPrice: null,
    validHighType: null,
    validLowAt: null,
    validLowPrice: null,
    validLowType: null,
    validStructureConfirmedAt: null,
    validStructureTrend: null,
  };
}

function canonicalTrendPoint(type, price, at, confirmedAt = null) {
  if (price === null || price === undefined || !Number.isFinite(Number(price)) || !at) {
    return null;
  }

  return {
    at,
    confirmedAt: confirmedAt ?? null,
    price: roundPrice(Number(price)),
    type,
  };
}

function canonicalTrendPointFromCandle(type, candle, field, confirmedAt = null) {
  return canonicalTrendPoint(type, field === 'high' ? candle.high : candle.low, candle.startAt, confirmedAt);
}

function withFlowPointStage(point, stage, extra = {}) {
  return point
    ? {
        ...cloneFlowPoint(point),
        ...extra,
        stage,
      }
    : null;
}

function canonicalTrendProvisionalPoint(type, candle, field) {
  return withFlowPointStage(canonicalTrendPointFromCandle(type, candle, field), 'provisional');
}

function canonicalTrendFinalPoint(point, confirmedAt) {
  return withFlowPointStage(point, 'final', { confirmedAt });
}

function canonicalTrendCloneState(state) {
  return {
    characterChange: cloneCharacterChange(state.characterChange),
    confirmedHH: cloneFlowPoint(state.confirmedHH),
    confirmedHL: cloneFlowPoint(state.confirmedHL),
    confirmedLH: cloneFlowPoint(state.confirmedLH),
    confirmedLL: cloneFlowPoint(state.confirmedLL),
    finalizationLevel: state.finalizationLevel ?? null,
    finalizationRule: state.finalizationRule ?? null,
    lastOfficialPivot: cloneFlowPoint(state.lastOfficialPivot),
    lastProcessedCandleAt: state.lastProcessedCandleAt ?? null,
    localFlow: {
      activePivot: cloneFlowPoint(state.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(state.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(state.localFlow?.internalTop),
    },
    mode: state.mode,
    activeSwing: cloneFlowPoint(state.activeSwing),
    pivotHistory: normalizePivotHistory(state.pivotHistory),
    reversal: state.reversal
      ? {
          bottom: cloneFlowPoint(state.reversal.bottom),
          low: cloneFlowPoint(state.reversal.low),
          stage: state.reversal.stage ?? null,
          top: cloneFlowPoint(state.reversal.top),
        }
      : null,
    preconfirmedAt: state.preconfirmedAt ?? null,
    preconfirmedPivot: cloneFlowPoint(state.preconfirmedPivot),
    preconfirmedReference: cloneFlowPoint(state.preconfirmedReference),
    sweep: clonePrimaryRangeSweep(state.sweep),
    trend: state.trend,
  };
}

function canonicalTrendInitialState(options = {}) {
  return {
    activeSwing: null,
    characterChange: null,
    confirmedHH: null,
    confirmedHL: null,
    confirmedLH: null,
    confirmedLL: null,
    finalizationLevel: null,
    finalizationRule: null,
    lastOfficialPivot: null,
    lastProcessedCandleAt: null,
    localFlow: {
      activePivot: null,
      internalBottom: null,
      internalSwing: null,
      internalTop: null,
    },
    mode: 'neutral_bootstrap',
    neutralHigh: null,
    neutralLow: null,
    pivotHistory: [],
    preconfirmedAt: null,
    preconfirmedPivot: null,
    preconfirmedReference: null,
    recordPivotHistory: options.recordPivotHistory === true,
    reversal: null,
    sweep: null,
    trend: 'neutral',
  };
}

function trendPivotHistoryKey(point) {
  return [point?.type ?? '', point?.at ?? '', point?.confirmedAt ?? '', point?.price ?? ''].join(':');
}

function normalizePivotHistory(value, limit = TREND_STRUCTURE_PIVOT_HISTORY_LIMIT) {
  const rawHistory = Array.isArray(value) ? value : [];
  const normalized = [];
  const keys = new Set();

  for (const item of rawHistory) {
    const point = cloneFlowPoint(item);

    if (!point?.type || point.price === null || point.price === undefined || !point.at) {
      continue;
    }

    const key = trendPivotHistoryKey(point);

    if (keys.has(key)) {
      continue;
    }

    keys.add(key);
    normalized.push(point);
  }

  return normalized
    .sort((left, right) => pointSortTime(left) - pointSortTime(right))
    .slice(-limit);
}

function appendPivotHistoryPoint(history, point, limit = TREND_STRUCTURE_PIVOT_HISTORY_LIMIT) {
  return normalizePivotHistory([...(Array.isArray(history) ? history : []), point], limit);
}

function canonicalTrendSetLastOfficialPivot(state, point) {
  state.lastOfficialPivot = cloneFlowPoint(point);

  if (state.recordPivotHistory) {
    state.pivotHistory = appendPivotHistoryPoint(state.pivotHistory, state.lastOfficialPivot);
  }
}

function canonicalTrendClearPreconfirmation(state) {
  state.finalizationLevel = null;
  state.finalizationRule = null;
  state.preconfirmedAt = null;
  state.preconfirmedPivot = null;
  state.preconfirmedReference = null;
}

function canonicalTrendSetPreconfirmedPivot(state, point, reference, candle, finalizationRule, finalizationLevel) {
  const preconfirmedAt = state.preconfirmedAt ?? candle.endAt;
  const preconfirmedReference = state.preconfirmedReference ?? cloneFlowPoint(reference);
  const nextPoint = withFlowPointStage(point, 'preconfirmed', {
    preconfirmedAt,
    preconfirmedReference,
  });

  state.activeSwing = nextPoint;
  state.preconfirmedAt = preconfirmedAt;
  state.preconfirmedPivot = cloneFlowPoint(nextPoint);
  state.preconfirmedReference = cloneFlowPoint(preconfirmedReference);
  state.finalizationRule = finalizationRule;
  state.finalizationLevel = roundPrice(finalizationLevel);
}

function canonicalTrendRefreshPreconfirmation(state, candle) {
  if (state.activeSwing?.stage !== 'preconfirmed') {
    return;
  }

  if (state.mode === 'bullish_tracking_hh') {
    state.finalizationRule = 'close_below_previous_low';
    state.finalizationLevel = roundPrice(candle.low);
  } else if (state.mode === 'bearish_tracking_ll') {
    state.finalizationRule = 'close_above_previous_high';
    state.finalizationLevel = roundPrice(candle.high);
  }

  state.preconfirmedPivot = cloneFlowPoint(state.activeSwing);
}

function canonicalTrendEmptyLocalFlow() {
  return {
    activePivot: null,
    internalBottom: null,
    internalSwing: null,
    internalTop: null,
  };
}

function canonicalTrendResetLocalFlow(state) {
  state.localFlow = canonicalTrendEmptyLocalFlow();
}

function isFlowPointAfterReference(point, reference) {
  if (!point || !reference) {
    return false;
  }

  return pointSortTime(point) > pointSortTime(reference);
}

function isPreChochPoint(point, candle) {
  return Boolean(point?.at && point.at !== candle.startAt);
}

function canonicalTrendMovementAxis(mode, lastOfficialPivot = null) {
  if (mode === 'bullish_tracking_hh' || mode === 'bearish_tracking_provisional_lh' || mode === 'choch_down_active') {
    return 'seeking_top';
  }

  if (mode === 'bullish_tracking_provisional_hl' || mode === 'bearish_tracking_ll' || mode === 'choch_up_active') {
    return 'seeking_bottom';
  }

  if (mode === 'reversal_pending') {
    return 'reversal_pending';
  }

  return movementAxisFromFlowPoint(lastOfficialPivot);
}

function canonicalTrendSequenceState(mode) {
  const states = {
    bearish_tracking_ll: 'tracking_lower_low',
    bearish_tracking_provisional_lh: 'tracking_provisional_lower_high',
    bullish_tracking_hh: 'tracking_higher_high',
    bullish_tracking_provisional_hl: 'tracking_provisional_higher_low',
    choch_down_active: 'choch_down_active',
    choch_up_active: 'choch_up_active',
    neutral_bootstrap: 'neutral_bootstrap',
    reversal_pending: 'reversal_pending',
  };

  return states[mode] ?? 'range';
}

function canonicalTrendPhase(mode, trend) {
  if (mode === 'choch_down_active') {
    return 'pivoting_down';
  }

  if (mode === 'choch_up_active') {
    return 'pivoting_up';
  }

  if (mode === 'bullish_tracking_provisional_hl') {
    return 'seeking_higher_low';
  }

  if (mode === 'bearish_tracking_provisional_lh') {
    return 'seeking_lower_high';
  }

  if (trend === 'neutral') {
    return 'range';
  }

  return 'range';
}

function canonicalTrendPrimaryRange(state) {
  const top = state.trend === 'bullish' ? state.confirmedHH : state.trend === 'bearish' ? state.confirmedLH : state.neutralHigh;
  const bottom = state.trend === 'bullish' ? state.confirmedHL : state.trend === 'bearish' ? state.confirmedLL : state.neutralLow;
  const lastPivot = state.lastOfficialPivot ?? latestConfirmedPivot(top, bottom);
  const movementAxis = canonicalTrendMovementAxis(state.mode, lastPivot);
  const rangeState =
    state.mode === 'choch_down_active' || state.mode === 'choch_up_active'
      ? 'choch_active'
      : state.mode === 'reversal_pending'
        ? 'reversal_pending'
        : movementAxis === 'seeking_top'
          ? 'seeking_top'
          : movementAxis === 'seeking_bottom'
            ? 'seeking_bottom'
            : 'inside_range';

  return {
    activePivot: cloneFlowPoint(state.activeSwing),
    activePrimaryPivot: cloneFlowPoint(state.activeSwing),
    activeSwing: cloneFlowPoint(state.activeSwing),
    anchorBottom: cloneFlowPoint(bottom),
    anchorTop: cloneFlowPoint(top),
    bottom: cloneFlowPoint(bottom),
    characterChange: cloneCharacterChange(state.characterChange),
    confirmedHH: cloneFlowPoint(state.confirmedHH),
    confirmedHL: cloneFlowPoint(state.confirmedHL),
    confirmedLH: cloneFlowPoint(state.confirmedLH),
    confirmedLL: cloneFlowPoint(state.confirmedLL),
    finalizationLevel: state.finalizationLevel ?? null,
    finalizationRule: state.finalizationRule ?? null,
    lastOfficialPivot: cloneFlowPoint(lastPivot),
    lastPivot: cloneFlowPoint(lastPivot),
    lastProcessedCandleAt: state.lastProcessedCandleAt,
    localFlow: {
      activePivot: cloneFlowPoint(state.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(state.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(state.localFlow?.internalTop),
    },
    mode: state.mode,
    movementAxis,
    pivotHistory: normalizePivotHistory(state.pivotHistory),
    preconfirmedAt: state.preconfirmedAt ?? null,
    preconfirmedPivot: cloneFlowPoint(state.preconfirmedPivot),
    preconfirmedReference: cloneFlowPoint(state.preconfirmedReference),
    reversal: state.reversal
      ? {
          bottom: cloneFlowPoint(state.reversal.bottom),
          low: cloneFlowPoint(state.reversal.low),
          stage: state.reversal.stage,
          top: cloneFlowPoint(state.reversal.top),
        }
      : null,
    state: rangeState,
    stateVersion: TREND_STRUCTURE_PRIMARY_RANGE_STATE_VERSION,
    sweep: clonePrimaryRangeSweep(state.sweep),
    top: cloneFlowPoint(top),
    trend: state.trend,
  };
}

function canonicalTrendPushEvent(events, candle, eventType, pivot, trend, reason, metadata = {}) {
  pushEvent(events, {
    candleEndAt: candle.endAt,
    candleStartAt: candle.startAt,
    eventType,
    metadata,
    pivotAt: pivot?.at ?? null,
    pivotPrice: pivot ? roundPrice(pivot.price) : null,
    pivotType: pivot?.type ?? null,
    reason,
    trend,
  });
}

function canonicalTrendCharacterChange(direction, referencePivot, candle, trend) {
  return {
    candle: {
      at: candle.startAt,
      close: roundPrice(candle.close),
      high: roundPrice(candle.high),
      low: roundPrice(candle.low),
    },
    confirmedAt: candle.endAt,
    confirmingPivot: null,
    direction,
    key: direction === 'up' ? 'choch_up' : 'choch_down',
    previousTrend: trend,
    reason:
      direction === 'up'
        ? 'ChoCH de alta confirmado por fechamento acima do ultimo LH confirmado.'
        : 'ChoCH de baixa confirmado por fechamento abaixo do ultimo HL confirmado.',
    referencePivot: cloneFlowPoint(referencePivot),
    state: 'confirmed',
    trend,
  };
}

function canonicalTrendTrackInternalBottom(state, point) {
  if (!point) {
    return;
  }

  const bottom = { ...cloneFlowPoint(point), type: LOW_PIVOT_TYPES.has(point.type) ? point.type : 'HL' };
  const current = state.localFlow?.internalBottom;

  if (!current || bottom.price < current.price) {
    state.localFlow.internalBottom = bottom;
    state.localFlow.internalSwing = bottom;
  }
}

function canonicalTrendTrackInternalTop(state, point) {
  if (!point) {
    return;
  }

  const top = { ...cloneFlowPoint(point), type: HIGH_PIVOT_TYPES.has(point.type) ? point.type : 'LH' };
  const current = state.localFlow?.internalTop;

  if (!current || top.price > current.price) {
    state.localFlow.internalTop = top;
    state.localFlow.internalSwing = top;
  }
}

function canonicalTrendActiveLow(state) {
  return state.activeSwing && LOW_PIVOT_TYPES.has(state.activeSwing.type)
    ? cloneFlowPoint(state.activeSwing)
    : null;
}

function canonicalTrendActiveHigh(state) {
  return state.activeSwing && HIGH_PIVOT_TYPES.has(state.activeSwing.type)
    ? cloneFlowPoint(state.activeSwing)
    : null;
}

function canonicalTrendUpdateActiveSwing(state, candle) {
  if (state.mode === 'bullish_tracking_hh') {
    if (!state.activeSwing || candle.high > state.activeSwing.price) {
      if (state.activeSwing?.stage === 'preconfirmed' || state.preconfirmedPivot) {
        canonicalTrendSetPreconfirmedPivot(
          state,
          canonicalTrendPointFromCandle('HH', candle, 'high'),
          state.preconfirmedReference ?? state.confirmedHH,
          candle,
          'close_below_previous_low',
          candle.low,
        );
      } else {
        state.activeSwing = canonicalTrendPointFromCandle('HH', candle, 'high');
      }
    }
    canonicalTrendRefreshPreconfirmation(state, candle);
  } else if (state.mode === 'bullish_tracking_provisional_hl') {
    if (!state.activeSwing || candle.low < state.activeSwing.price) {
      state.activeSwing = canonicalTrendProvisionalPoint('HL', candle, 'low');
    }
    canonicalTrendClearPreconfirmation(state);
    canonicalTrendTrackInternalTop(state, canonicalTrendPointFromCandle('LH', candle, 'high'));
  } else if (state.mode === 'choch_down_active') {
    if (!state.activeSwing || candle.high > state.activeSwing.price) {
      state.activeSwing = canonicalTrendProvisionalPoint('LH', candle, 'high');
    }
    canonicalTrendClearPreconfirmation(state);
  } else if (state.mode === 'bearish_tracking_ll') {
    if (!state.activeSwing || candle.low < state.activeSwing.price) {
      if (state.activeSwing?.stage === 'preconfirmed' || state.preconfirmedPivot) {
        canonicalTrendSetPreconfirmedPivot(
          state,
          canonicalTrendPointFromCandle('LL', candle, 'low'),
          state.preconfirmedReference ?? state.confirmedLL,
          candle,
          'close_above_previous_high',
          candle.high,
        );
      } else {
        state.activeSwing = canonicalTrendPointFromCandle('LL', candle, 'low');
      }
    }
    canonicalTrendRefreshPreconfirmation(state, candle);
  } else if (state.mode === 'bearish_tracking_provisional_lh') {
    if (!state.activeSwing || candle.high > state.activeSwing.price) {
      state.activeSwing = canonicalTrendProvisionalPoint('LH', candle, 'high');
    }
    canonicalTrendClearPreconfirmation(state);
    canonicalTrendTrackInternalBottom(state, canonicalTrendPointFromCandle('HL', candle, 'low'));
  } else if (state.mode === 'choch_up_active') {
    if (!state.activeSwing || candle.low < state.activeSwing.price) {
      state.activeSwing = canonicalTrendProvisionalPoint('HL', candle, 'low');
    }
    canonicalTrendClearPreconfirmation(state);
  }
}

function canonicalTrendActivateChochDown(state, candle, events) {
  const chochLow =
    state.activeSwing && LOW_PIVOT_TYPES.has(state.activeSwing.type)
      ? { ...cloneFlowPoint(state.activeSwing), type: 'HL' }
      : canonicalTrendPointFromCandle('HL', candle, 'low');
  const internalTop =
    state.localFlow?.internalTop &&
      isFlowPointAfterReference(state.localFlow.internalTop, state.confirmedHL) &&
      isPreChochPoint(state.localFlow.internalTop, candle)
      ? { ...cloneFlowPoint(state.localFlow.internalTop), type: 'LH' }
      : state.activeSwing &&
          HIGH_PIVOT_TYPES.has(state.activeSwing.type) &&
          isFlowPointAfterReference(state.activeSwing, state.confirmedHL) &&
          isPreChochPoint(state.activeSwing, candle)
        ? { ...cloneFlowPoint(state.activeSwing), type: 'LH' }
        : state.localFlow?.internalSwing &&
            HIGH_PIVOT_TYPES.has(state.localFlow.internalSwing.type) &&
            isFlowPointAfterReference(state.localFlow.internalSwing, state.confirmedHL) &&
            isPreChochPoint(state.localFlow.internalSwing, candle)
        ? { ...cloneFlowPoint(state.localFlow.internalSwing), type: 'LH' }
        : null;

  state.characterChange = canonicalTrendCharacterChange('down', state.confirmedHL, candle, 'bullish');
  state.mode = 'choch_down_active';
  state.reversal = {
    low: chochLow,
    stage: 'seeking_lower_high',
    top: internalTop,
  };
  state.activeSwing = canonicalTrendPointFromCandle('LH', candle, 'high');
  state.sweep = null;
  canonicalTrendResetLocalFlow(state);
  canonicalTrendPushEvent(
    events,
    candle,
    'character_change',
    state.confirmedHL,
    state.trend,
    state.characterChange.reason,
    { characterChange: state.characterChange, reasonKey: 'choch_down_confirmed' },
  );
}

function canonicalTrendActivateChochUp(state, candle, events) {
  const chochTop =
    state.activeSwing && HIGH_PIVOT_TYPES.has(state.activeSwing.type)
      ? { ...cloneFlowPoint(state.activeSwing), type: 'LH' }
      : canonicalTrendPointFromCandle('LH', candle, 'high');
  const internalBottom =
    state.localFlow?.internalBottom &&
      isFlowPointAfterReference(state.localFlow.internalBottom, state.confirmedLH) &&
      isPreChochPoint(state.localFlow.internalBottom, candle)
      ? { ...cloneFlowPoint(state.localFlow.internalBottom), type: 'HL' }
      : state.activeSwing &&
          LOW_PIVOT_TYPES.has(state.activeSwing.type) &&
          isFlowPointAfterReference(state.activeSwing, state.confirmedLH) &&
          isPreChochPoint(state.activeSwing, candle)
        ? { ...cloneFlowPoint(state.activeSwing), type: 'HL' }
        : state.localFlow?.internalSwing &&
            LOW_PIVOT_TYPES.has(state.localFlow.internalSwing.type) &&
            isFlowPointAfterReference(state.localFlow.internalSwing, state.confirmedLH) &&
            isPreChochPoint(state.localFlow.internalSwing, candle)
        ? { ...cloneFlowPoint(state.localFlow.internalSwing), type: 'HL' }
        : null;

  state.characterChange = canonicalTrendCharacterChange('up', state.confirmedLH, candle, 'bearish');
  state.mode = 'choch_up_active';
  state.reversal = {
    bottom: internalBottom,
    stage: 'seeking_higher_low',
    top: chochTop,
  };
  state.activeSwing = canonicalTrendPointFromCandle('HL', candle, 'low');
  state.sweep = null;
  canonicalTrendResetLocalFlow(state);
  canonicalTrendPushEvent(
    events,
    candle,
    'character_change',
    state.confirmedLH,
    state.trend,
    state.characterChange.reason,
    { characterChange: state.characterChange, reasonKey: 'choch_up_confirmed' },
  );
}

function canonicalTrendStartBullish(state, high, low, candle, events) {
  state.trend = 'bullish';
  state.mode = 'bullish_tracking_hh';
  state.confirmedHH = canonicalTrendFinalPoint({ ...cloneFlowPoint(high), type: 'HH' }, candle.endAt);
  state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(low), type: 'HL' }, candle.endAt);
  canonicalTrendSetLastOfficialPivot(state, state.confirmedHL);
  state.activeSwing = canonicalTrendPointFromCandle('HH', candle, 'high');
  state.characterChange = null;
  state.reversal = null;
  canonicalTrendResetLocalFlow(state);
  canonicalTrendPushEvent(
    events,
    candle,
    'pivot_confirmed',
    state.confirmedHL,
    state.trend,
    'Pivo de alta inicial confirmado por fechamento acima do HH de referencia.',
    { validStructureConfirmed: true, reasonKey: 'initial_bullish_pivot_confirmed' },
  );
}

function canonicalTrendStartBearish(state, high, low, candle, events) {
  state.trend = 'bearish';
  state.mode = 'bearish_tracking_ll';
  state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(high), type: 'LH' }, candle.endAt);
  state.confirmedLL = canonicalTrendFinalPoint({ ...cloneFlowPoint(low), type: 'LL' }, candle.endAt);
  canonicalTrendSetLastOfficialPivot(state, state.confirmedLH);
  state.activeSwing = canonicalTrendPointFromCandle('LL', candle, 'low');
  state.characterChange = null;
  state.reversal = null;
  canonicalTrendResetLocalFlow(state);
  canonicalTrendPushEvent(
    events,
    candle,
    'pivot_confirmed',
    state.confirmedLH,
    state.trend,
    'Pivo de baixa inicial confirmado por fechamento abaixo do LL de referencia.',
    { validStructureConfirmed: true, reasonKey: 'initial_bearish_pivot_confirmed' },
  );
}

function canonicalTrendHandleNeutralBreaks(state, candle, events) {
  if (
    state.neutralHigh &&
    state.neutralLow &&
    pointSortTime(state.neutralLow) > pointSortTime(state.neutralHigh) &&
    candle.close > state.neutralHigh.price
  ) {
    canonicalTrendStartBullish(state, state.neutralHigh, state.neutralLow, candle, events);
    return true;
  }

  if (
    state.neutralHigh &&
    state.neutralLow &&
    pointSortTime(state.neutralHigh) > pointSortTime(state.neutralLow) &&
    candle.close < state.neutralLow.price
  ) {
    canonicalTrendStartBearish(state, state.neutralHigh, state.neutralLow, candle, events);
    return true;
  }

  return false;
}

function canonicalTrendHandleStructuralBreaks(state, candle, events) {
  if (state.trend === 'neutral') {
    return canonicalTrendHandleNeutralBreaks(state, candle, events);
  }

  if (state.mode === 'bullish_tracking_provisional_hl') {
    if (state.confirmedHL && candle.low < state.confirmedHL.price && candle.close >= state.confirmedHL.price) {
      state.sweep = primaryRangeSweepFromCandle('down', candle, state.confirmedHL);
    }

    if (state.confirmedHL && candle.close < state.confirmedHL.price) {
      canonicalTrendActivateChochDown(state, candle, events);
      return true;
    }

    if (state.confirmedHH && state.activeSwing && candle.close > state.confirmedHH.price) {
      state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(state.activeSwing), type: 'HL' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedHL);
      state.mode = 'bullish_tracking_hh';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('HH', candle, 'high'),
        state.confirmedHH,
        candle,
        'close_below_previous_low',
        candle.low,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedHL,
        state.trend,
        'HL confirmado por fechamento acima do ultimo HH confirmado.',
        { validStructureConfirmed: true, reasonKey: 'higher_low_confirmed_by_hh_break' },
      );
      return true;
    }
  }

  if (state.mode === 'bullish_tracking_hh') {
    if (state.confirmedHL && candle.low < state.confirmedHL.price && candle.close >= state.confirmedHL.price) {
      state.sweep = primaryRangeSweepFromCandle('down', candle, state.confirmedHL);
    }

    if (state.confirmedHL && candle.close < state.confirmedHL.price) {
      canonicalTrendActivateChochDown(state, candle, events);
      return true;
    }

    if (state.confirmedHH && candle.high > state.confirmedHH.price && candle.close <= state.confirmedHH.price) {
      state.sweep = primaryRangeSweepFromCandle('up', candle, state.confirmedHH);
    }
  }

  if (state.mode === 'choch_down_active') {
    const chochLow = state.reversal?.low ?? state.reversal?.bottom;
    const bearishReversalTop = state.reversal?.top ?? canonicalTrendActiveHigh(state);

    if (state.confirmedHH && chochLow && candle.close > state.confirmedHH.price) {
      state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(chochLow), type: 'HL' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedHL);
      state.mode = 'bullish_tracking_hh';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('HH', candle, 'high'),
        state.confirmedHH,
        candle,
        'close_below_previous_low',
        candle.low,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedHL,
        state.trend,
        'HL confirmado apos falha de tendencia por recuperacao acima do HH.',
        { validStructureConfirmed: true, reasonKey: 'higher_low_recovered_after_choch_down' },
      );
      return true;
    }

    if (bearishReversalTop && chochLow && candle.close < chochLow.price) {
      state.trend = 'bearish';
      state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(bearishReversalTop), type: 'LH' }, candle.endAt);
      state.confirmedLL = canonicalTrendFinalPoint(canonicalTrendPointFromCandle('LL', candle, 'low'), candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedLL);
      state.activeSwing = cloneFlowPoint(state.confirmedLL);
      state.mode = 'bearish_tracking_ll';
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'trend_changed',
        state.confirmedLL,
        state.trend,
        'Inversao para baixa confirmada por pivo completo apos ChoCH.',
        { validStructureConfirmed: true, reasonKey: 'bearish_reversal_after_choch_down' },
      );
      return true;
    }
  }

  if (state.mode === 'reversal_pending' && state.trend === 'bullish') {
    const bearishAttemptLow = state.reversal?.low ?? state.reversal?.bottom;
    const bearishAttemptTop = state.reversal?.top;

    if (state.confirmedHH && bearishAttemptLow && candle.close > state.confirmedHH.price) {
      state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(bearishAttemptLow), type: 'HL' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedHL);
      state.mode = 'bullish_tracking_hh';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('HH', candle, 'high'),
        state.confirmedHH,
        candle,
        'close_below_previous_low',
        candle.low,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedHL,
        state.trend,
        'HL confirmado apos tentativa de inversao por recuperacao acima do HH.',
        { validStructureConfirmed: true, reasonKey: 'higher_low_recovered_after_reversal_pending' },
      );
      return true;
    }

    if (bearishAttemptTop && bearishAttemptLow && candle.close < bearishAttemptLow.price) {
      state.trend = 'bearish';
      state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(bearishAttemptTop), type: 'LH' }, candle.endAt);
      state.confirmedLL = canonicalTrendFinalPoint(canonicalTrendPointFromCandle('LL', candle, 'low'), candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedLL);
      state.activeSwing = cloneFlowPoint(state.confirmedLL);
      state.mode = 'bearish_tracking_ll';
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'trend_changed',
        state.confirmedLL,
        state.trend,
        'Inversao para baixa confirmada por pivo completo apos ChoCH.',
        { validStructureConfirmed: true, reasonKey: 'bearish_reversal_after_choch_down' },
      );
      return true;
    }
  }

  if (state.mode === 'bearish_tracking_provisional_lh') {
    if (state.confirmedLH && candle.high > state.confirmedLH.price && candle.close <= state.confirmedLH.price) {
      state.sweep = primaryRangeSweepFromCandle('up', candle, state.confirmedLH);
    }

    if (state.confirmedLH && candle.close > state.confirmedLH.price) {
      canonicalTrendActivateChochUp(state, candle, events);
      return true;
    }

    if (state.confirmedLL && state.activeSwing && candle.close < state.confirmedLL.price) {
      state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(state.activeSwing), type: 'LH' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedLH);
      state.mode = 'bearish_tracking_ll';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('LL', candle, 'low'),
        state.confirmedLL,
        candle,
        'close_above_previous_high',
        candle.high,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedLH,
        state.trend,
        'LH confirmado por fechamento abaixo do ultimo LL confirmado.',
        { validStructureConfirmed: true, reasonKey: 'lower_high_confirmed_by_ll_break' },
      );
      return true;
    }
  }

  if (state.mode === 'bearish_tracking_ll') {
    if (state.confirmedLH && candle.high > state.confirmedLH.price && candle.close <= state.confirmedLH.price) {
      state.sweep = primaryRangeSweepFromCandle('up', candle, state.confirmedLH);
    }

    if (state.confirmedLH && candle.close > state.confirmedLH.price) {
      canonicalTrendActivateChochUp(state, candle, events);
      return true;
    }

    if (state.confirmedLL && candle.low < state.confirmedLL.price && candle.close >= state.confirmedLL.price) {
      state.sweep = primaryRangeSweepFromCandle('down', candle, state.confirmedLL);
    }
  }

  if (state.mode === 'choch_up_active') {
    const chochTop = state.reversal?.top;
    const bullishReversalBottom = state.reversal?.bottom ?? canonicalTrendActiveLow(state);

    if (state.confirmedLL && chochTop && candle.close < state.confirmedLL.price) {
      state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(chochTop), type: 'LH' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedLH);
      state.mode = 'bearish_tracking_ll';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('LL', candle, 'low'),
        state.confirmedLL,
        candle,
        'close_above_previous_high',
        candle.high,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedLH,
        state.trend,
        'LH confirmado apos falha de tendencia por recuperacao abaixo do LL.',
        { validStructureConfirmed: true, reasonKey: 'lower_high_recovered_after_choch_up' },
      );
      return true;
    }

    if (bullishReversalBottom && chochTop && candle.close > chochTop.price) {
      state.trend = 'bullish';
      state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(bullishReversalBottom), type: 'HL' }, candle.endAt);
      state.confirmedHH = canonicalTrendFinalPoint(canonicalTrendPointFromCandle('HH', candle, 'high'), candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedHH);
      state.activeSwing = cloneFlowPoint(state.confirmedHH);
      state.mode = 'bullish_tracking_hh';
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'trend_changed',
        state.confirmedHH,
        state.trend,
        'Inversao para alta confirmada por pivo completo apos ChoCH.',
        { validStructureConfirmed: true, reasonKey: 'bullish_reversal_after_choch_up' },
      );
      return true;
    }
  }

  if (state.mode === 'reversal_pending' && state.trend === 'bearish') {
    const bullishAttemptBottom = state.reversal?.bottom ?? state.reversal?.low;
    const bullishAttemptTop = state.reversal?.top;

    if (state.confirmedLL && bullishAttemptTop && candle.close < state.confirmedLL.price) {
      state.confirmedLH = canonicalTrendFinalPoint({ ...cloneFlowPoint(bullishAttemptTop), type: 'LH' }, candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedLH);
      state.mode = 'bearish_tracking_ll';
      canonicalTrendSetPreconfirmedPivot(
        state,
        canonicalTrendPointFromCandle('LL', candle, 'low'),
        state.confirmedLL,
        candle,
        'close_above_previous_high',
        candle.high,
      );
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'pivot_confirmed',
        state.confirmedLH,
        state.trend,
        'LH confirmado apos tentativa de inversao por recuperacao abaixo do LL.',
        { validStructureConfirmed: true, reasonKey: 'lower_high_recovered_after_reversal_pending' },
      );
      return true;
    }

    if (bullishAttemptBottom && bullishAttemptTop && candle.close > bullishAttemptTop.price) {
      state.trend = 'bullish';
      state.confirmedHL = canonicalTrendFinalPoint({ ...cloneFlowPoint(bullishAttemptBottom), type: 'HL' }, candle.endAt);
      state.confirmedHH = canonicalTrendFinalPoint(canonicalTrendPointFromCandle('HH', candle, 'high'), candle.endAt);
      canonicalTrendSetLastOfficialPivot(state, state.confirmedHH);
      state.activeSwing = cloneFlowPoint(state.confirmedHH);
      state.mode = 'bullish_tracking_hh';
      state.characterChange = null;
      state.reversal = null;
      state.sweep = null;
      canonicalTrendResetLocalFlow(state);
      canonicalTrendPushEvent(
        events,
        candle,
        'trend_changed',
        state.confirmedHH,
        state.trend,
        'Inversao para alta confirmada por pivo completo apos ChoCH.',
        { validStructureConfirmed: true, reasonKey: 'bullish_reversal_after_choch_up' },
      );
      return true;
    }
  }

  return false;
}

function canonicalTrendHandleLocalTop(state, point, candle, events) {
  if (state.trend === 'neutral') {
    state.neutralHigh = point;
    return;
  }

  if (state.mode === 'bullish_tracking_hh') {
    const officialCandidate = state.activeSwing ?? point;
    if (state.confirmedHH && officialCandidate && officialCandidate.price < state.confirmedHH.price) {
      canonicalTrendTrackInternalTop(state, { ...cloneFlowPoint(point), type: 'LH' });
      return;
    }

    state.confirmedHH = canonicalTrendFinalPoint(
      { ...cloneFlowPoint(state.activeSwing ?? point), type: 'HH' },
      candle.endAt,
    );
    canonicalTrendSetLastOfficialPivot(state, state.confirmedHH);
    state.activeSwing = canonicalTrendProvisionalPoint('HL', candle, 'low');
    state.mode = 'bullish_tracking_provisional_hl';
    state.sweep = null;
    canonicalTrendClearPreconfirmation(state);
    canonicalTrendResetLocalFlow(state);
    canonicalTrendPushEvent(
      events,
      candle,
      'pivot_confirmed',
      state.confirmedHH,
      state.trend,
      'HH em formacao confirmado por fechamento abaixo da minima anterior.',
      { validStructureConfirmed: true, reasonKey: 'higher_high_formation_confirmed' },
    );
    return;
  }

  if (state.mode === 'choch_down_active' && state.confirmedHH && point.price < state.confirmedHH.price) {
    const currentTop = state.reversal?.top;
    const reversalTop =
      currentTop && point.price <= currentTop.price
        ? cloneFlowPoint(currentTop)
        : { ...cloneFlowPoint(point), type: 'LH' };
    state.reversal = {
      low: cloneFlowPoint(state.reversal?.low ?? state.reversal?.bottom),
      stage: 'awaiting_lower_low_break',
      top: reversalTop,
    };
    state.mode = 'reversal_pending';
    state.localFlow.internalSwing = cloneFlowPoint(state.reversal.top);
    return;
  }

  state.localFlow.internalSwing = cloneFlowPoint(point);
}

function canonicalTrendHandleLocalBottom(state, point, candle, events) {
  if (state.trend === 'neutral') {
    state.neutralLow = point;
    return;
  }

  if (state.mode === 'bearish_tracking_ll') {
    const officialCandidate = state.activeSwing ?? point;
    if (state.confirmedLL && officialCandidate && officialCandidate.price > state.confirmedLL.price) {
      canonicalTrendTrackInternalBottom(state, { ...cloneFlowPoint(point), type: 'HL' });
      return;
    }

    state.confirmedLL = canonicalTrendFinalPoint(
      { ...cloneFlowPoint(state.activeSwing ?? point), type: 'LL' },
      candle.endAt,
    );
    canonicalTrendSetLastOfficialPivot(state, state.confirmedLL);
    state.activeSwing = canonicalTrendProvisionalPoint('LH', candle, 'high');
    state.mode = 'bearish_tracking_provisional_lh';
    state.sweep = null;
    canonicalTrendClearPreconfirmation(state);
    canonicalTrendResetLocalFlow(state);
    canonicalTrendPushEvent(
      events,
      candle,
      'pivot_confirmed',
      state.confirmedLL,
      state.trend,
      'LL em formacao confirmado por fechamento acima da maxima anterior.',
      { validStructureConfirmed: true, reasonKey: 'lower_low_formation_confirmed' },
    );
    return;
  }

  if (state.mode === 'choch_up_active' && state.confirmedLL && point.price > state.confirmedLL.price) {
    const currentBottom = state.reversal?.bottom ?? state.reversal?.low;
    const reversalBottom =
      currentBottom && point.price >= currentBottom.price
        ? cloneFlowPoint(currentBottom)
        : { ...cloneFlowPoint(point), type: 'HL' };
    state.reversal = {
      bottom: reversalBottom,
      stage: 'awaiting_higher_high_break',
      top: cloneFlowPoint(state.reversal?.top),
    };
    state.mode = 'reversal_pending';
    state.localFlow.internalSwing = cloneFlowPoint(state.reversal.bottom);
    return;
  }

  state.localFlow.internalSwing = cloneFlowPoint(point);
}

function calculateCanonicalTrendStructureV4(inputCandles) {
  const candles = normalizeTrendStructureCandles(inputCandles);
  const candleCount = candles.length;
  const lastCandleAt = candleCount > 0 ? candles[candleCount - 1].endAt : null;

  if (candleCount === 0) {
    return {
      events: [],
      needsRebuild: false,
      snapshot: buildEmptySnapshot(0, 'no_data'),
    };
  }

  if (candleCount < TREND_STRUCTURE_MIN_READY_CANDLES) {
    return {
      events: [],
      needsRebuild: false,
      snapshot: buildEmptySnapshot(candleCount, 'insufficient_data', lastCandleAt),
    };
  }

  const events = [];
  const state = canonicalTrendInitialState();
  let localSeeking = null;
  let localActive = null;

  for (let index = 1; index < candles.length; index += 1) {
    const previous = candles[index - 1];
    const current = candles[index];
    state.lastProcessedCandleAt = current.endAt;
    state.sweep = null;

    canonicalTrendUpdateActiveSwing(state, current);
    const structuralChanged = canonicalTrendHandleStructuralBreaks(state, current, events);

    if (structuralChanged) {
      const tracker = canonicalTrendLocalTrackerForMode(state, current);
      localSeeking = tracker.localSeeking;
      localActive = tracker.localActive;
      continue;
    }

    if (!localSeeking) {
      if (current.close > previous.high) {
        const source = previous.low <= current.low ? previous : current;
        localSeeking = 'top';
        localActive = buildRawPivot(source, 'L', 'low', current.endAt);
        canonicalTrendHandleLocalBottom(state, stripRawPivot(localActive, 'L'), current, events);
      } else if (current.close < previous.low) {
        const source = previous.high >= current.high ? previous : current;
        localSeeking = 'bottom';
        localActive = buildRawPivot(source, 'H', 'high', current.endAt);
        canonicalTrendHandleLocalTop(state, stripRawPivot(localActive, 'H'), current, events);
      }

      continue;
    }

    if (localSeeking === 'top') {
      if (!localActive || current.high > localActive.price) {
        localActive = buildRawPivot(current, 'H', 'high', current.endAt);
      }

      if (current.close < previous.low && localActive) {
        const topPoint = stripRawPivot(
          {
            ...localActive,
            confirmedAt: current.endAt,
          },
          'H',
        );
        canonicalTrendHandleLocalTop(state, topPoint, current, events);
        localSeeking = 'bottom';
        localActive = buildRawPivot(current, 'L', 'low', current.endAt);
      }

      continue;
    }

    if (!localActive || current.low < localActive.price) {
      localActive = buildRawPivot(current, 'L', 'low', current.endAt);
    }

    if (current.close > previous.high && localActive) {
      const bottomPoint = stripRawPivot(
        {
          ...localActive,
          confirmedAt: current.endAt,
        },
        'L',
      );
      canonicalTrendHandleLocalBottom(state, bottomPoint, current, events);
      localSeeking = 'top';
      localActive = buildRawPivot(current, 'H', 'high', current.endAt);
    }
  }

  return {
    events,
    needsRebuild: false,
    snapshot: snapshotFromCanonicalTrendStateV4(candleCount, lastCandleAt, state),
  };
}

function snapshotFromCanonicalTrendStateV4(candleCount, lastCandleAt, rawState) {
  const state = canonicalTrendCloneState(rawState);
  const primaryRange = canonicalTrendPrimaryRange(state);
  const top = primaryRange.top;
  const bottom = primaryRange.bottom;
  const lastPivot = primaryRange.lastOfficialPivot ?? primaryRange.lastPivot;
  const activePivot = primaryRange.activePivot;
  const preconfirmedPivot = primaryRange.preconfirmedPivot;
  const legacyCandidatePivot =
    activePivot?.type === 'HL' || activePivot?.type === 'LH' ? activePivot : null;
  const movementAxis = primaryRange.movementAxis;
  const flow = {
    activePivot: cloneFlowPoint(activePivot),
    characterChange: cloneCharacterChange(primaryRange.characterChange),
    confirmationLevel: primaryRange.finalizationLevel ?? activePivot?.price ?? null,
    confirmationRule: preconfirmedPivot
      ? primaryRange.finalizationRule
      : activePivot?.type === 'HL'
        ? 'close_above_confirmed_hh'
        : activePivot?.type === 'LH'
          ? 'close_below_confirmed_ll'
          : null,
    confirmedBottom: cloneFlowPoint(bottom),
    confirmedTop: cloneFlowPoint(top),
    lastConfirmedPivot: cloneFlowPoint(lastPivot),
    localFlow: {
      activePivot: cloneFlowPoint(state.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(state.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(state.localFlow?.internalTop),
    },
    movementAxis,
    nextExpectedPivot:
      movementAxis === 'seeking_top'
        ? state.trend === 'bullish'
          ? 'HH'
          : 'LH'
        : movementAxis === 'seeking_bottom'
          ? state.trend === 'bullish'
            ? 'HL'
            : 'LL'
          : null,
    pivotOptions:
      movementAxis === 'seeking_top'
        ? ['HH', 'LH']
        : movementAxis === 'seeking_bottom'
          ? ['HL', 'LL']
          : [],
    preconfirmedAt: primaryRange.preconfirmedAt ?? null,
    preconfirmedPivot: cloneFlowPoint(preconfirmedPivot),
    preconfirmedReference: cloneFlowPoint(primaryRange.preconfirmedReference),
    primaryRange,
    primaryStructure: {
      activePivot: cloneFlowPoint(activePivot),
      bottom: cloneFlowPoint(bottom),
      lastPivot: cloneFlowPoint(lastPivot),
      movementAxis,
      top: cloneFlowPoint(top),
    },
    reversalBias:
      primaryRange.characterChange?.key === 'choch_down'
        ? 'bearish_attempt'
        : primaryRange.characterChange?.key === 'choch_up'
          ? 'bullish_attempt'
          : 'none',
    reversalPivotStrength: 'none',
    reversalPivotStrengthReason: null,
    sequenceState: canonicalTrendSequenceState(state.mode),
    trend: state.trend,
  };
  const snapshot = {
    ...buildEmptySnapshot(candleCount, 'ready', lastCandleAt),
    candidatePivotPrice: legacyCandidatePivot ? roundPrice(legacyCandidatePivot.price) : null,
    candidatePivotType: legacyCandidatePivot?.type ?? null,
    candidateStartedAt: legacyCandidatePivot?.at ?? null,
    flow,
    hhAt: state.trend === 'bullish' && state.confirmedHH ? state.confirmedHH.at : null,
    hhPrice: state.trend === 'bullish' && state.confirmedHH ? state.confirmedHH.price : null,
    hlAt: state.trend === 'bullish' && state.confirmedHL ? state.confirmedHL.at : null,
    hlPrice: state.trend === 'bullish' && state.confirmedHL ? state.confirmedHL.price : null,
    lastPivotAt: lastPivot?.at ?? null,
    lastPivotPrice: lastPivot ? roundPrice(lastPivot.price) : null,
    lastPivotType: lastPivot?.type ?? null,
    lhAt: state.trend === 'bearish' && state.confirmedLH ? state.confirmedLH.at : null,
    lhPrice: state.trend === 'bearish' && state.confirmedLH ? state.confirmedLH.price : null,
    llAt: state.trend === 'bearish' && state.confirmedLL ? state.confirmedLL.at : null,
    llPrice: state.trend === 'bearish' && state.confirmedLL ? state.confirmedLL.price : null,
    phase: canonicalTrendPhase(state.mode, state.trend),
    provisionalPivotAt: legacyCandidatePivot?.at ?? null,
    provisionalPivotConfirmation: legacyCandidatePivot ? flow.confirmationRule : null,
    provisionalPivotPrice: legacyCandidatePivot ? roundPrice(legacyCandidatePivot.price) : null,
    provisionalPivotType: legacyCandidatePivot?.type ?? null,
    trend: state.trend,
    validHighAt: top?.at ?? null,
    validHighPrice: top ? roundPrice(top.price) : null,
    validHighType: top?.type ?? null,
    validLowAt: bottom?.at ?? null,
    validLowPrice: bottom ? roundPrice(bottom.price) : null,
    validLowType: bottom?.type ?? null,
    validStructureConfirmedAt: lastPivot?.confirmedAt ?? lastPivot?.at ?? null,
    validStructureTrend: state.trend,
  };

  return snapshot;
}

function eventKey(event) {
  return [
    event.eventType,
    event.pivotType ?? '',
    event.pivotAt ?? '',
    event.candleEndAt ?? '',
    event.trend ?? '',
    event.reason ?? '',
  ].join(':');
}

function pushEvent(events, event) {
  const metadata = {
    reasonKey: event.metadata?.reasonKey ?? event.reasonKey ?? event.eventType,
    ...(event.metadata ?? {}),
  };

  events.push({
    eventKey: eventKey(event),
    phase: null,
    pivotAt: null,
    pivotPrice: null,
    pivotType: null,
    trend: null,
    ...event,
    metadata,
  });
}

function cloneFlowPoint(point) {
  if (!point) {
    return null;
  }

  const cloned = {
    at: point.at,
    confirmedAt: point.confirmedAt ?? null,
    price: roundPrice(point.price),
    type: point.type,
  };

  if (point.stage) {
    cloned.stage = point.stage;
  }

  if (point.preconfirmedAt) {
    cloned.preconfirmedAt = point.preconfirmedAt;
  }

  if (point.preconfirmedReference) {
    cloned.preconfirmedReference = cloneFlowPoint(point.preconfirmedReference);
  }

  if (point.locatedAt) {
    cloned.locatedAt = point.locatedAt;
  }

  if (point.promotedAt) {
    cloned.promotedAt = point.promotedAt;
  }

  if (point.promotionSource) {
    cloned.promotionSource = point.promotionSource;
  }

  if (point.field) {
    cloned.field = point.field;
  }

  if (point.scope) {
    cloned.scope = point.scope;
  }

  if (point.eventType) {
    cloned.eventType = point.eventType;
  }

  if (point.promotionReason) {
    cloned.promotionReason = point.promotionReason;
  }

  if (point.structureState) {
    cloned.structureState = point.structureState;
  }

  return cloned;
}

function pointFromCandle(candle, type, field) {
  if (!candle || (field !== 'high' && field !== 'low')) {
    return null;
  }

  return {
    at: candle.startAt,
    price: roundPrice(candle[field]),
    type,
  };
}

function buildRawPivot(candle, type, field, confirmedAt) {
  const point = pointFromCandle(candle, type, field);

  return point
    ? {
        ...point,
        confirmedAt,
        field,
      }
    : null;
}

function stripRawPivot(point, type = point?.type) {
  if (!point) {
    return null;
  }

  return {
    at: point.at,
    confirmedAt: point.confirmedAt ?? null,
    price: roundPrice(point.price),
    type,
  };
}

function canonicalTrendLocalTrackerForMode(state, candle) {
  if (!state || !candle) {
    return { localActive: null, localSeeking: null };
  }

  if (state.mode === 'bullish_tracking_hh') {
    return {
      localActive: buildRawPivot(candle, 'H', 'high', candle.endAt),
      localSeeking: 'top',
    };
  }

  if (state.mode === 'bullish_tracking_provisional_hl' || state.mode === 'choch_up_active') {
    return {
      localActive: buildRawPivot(candle, 'L', 'low', candle.endAt),
      localSeeking: 'bottom',
    };
  }

  if (state.mode === 'bearish_tracking_ll') {
    return {
      localActive: buildRawPivot(candle, 'L', 'low', candle.endAt),
      localSeeking: 'bottom',
    };
  }

  if (state.mode === 'bearish_tracking_provisional_lh' || state.mode === 'choch_down_active') {
    return {
      localActive: buildRawPivot(candle, 'H', 'high', candle.endAt),
      localSeeking: 'top',
    };
  }

  if (state.mode === 'reversal_pending') {
    return state.trend === 'bullish'
      ? {
          localActive: buildRawPivot(candle, 'L', 'low', candle.endAt),
          localSeeking: 'bottom',
        }
      : {
          localActive: buildRawPivot(candle, 'H', 'high', candle.endAt),
          localSeeking: 'top',
        };
  }

  return { localActive: null, localSeeking: null };
}

function latestConfirmedPivot(left, right) {
  if (!left) {
    return right;
  }

  if (!right) {
    return left;
  }

  return Date.parse(right.confirmedAt ?? right.at ?? 0) > Date.parse(left.confirmedAt ?? left.at ?? 0)
    ? right
    : left;
}

function movementAxisFromFlowPoint(point) {
  if (!point) {
    return 'inside_range';
  }

  return point.field === 'low' || LOW_PIVOT_TYPES.has(point.type)
    ? 'seeking_top'
    : 'seeking_bottom';
}

function pointSortTime(point) {
  return Date.parse(point?.confirmedAt ?? point?.at ?? 0);
}

function candidateOfficialEmptyState() {
  return {
    activeExtreme: null,
    characterChange: null,
    currentCandidate: null,
    lastOfficialPivot: null,
    lastProcessedCandleAt: null,
    localFlow: canonicalTrendEmptyLocalFlow(),
    officialBottom: null,
    officialTop: null,
    protectedBottom: null,
    protectedTop: null,
    pivotsByType: {
      HH: null,
      HL: null,
      LH: null,
      LL: null,
    },
    pivotLedger: [],
    pivotHistory: [],
    recordPivotHistory: false,
    reversalCandidate: null,
    requiredTopCorrectionBreakPrice: null,
    requiresCorrectionBeforeBottomCandidate: false,
    requiresCorrectionBeforeTopCandidate: false,
    seeking: null,
    structuralEvents: [],
    sweep: null,
    trend: 'neutral',
  };
}

function candidateOfficialPivotLedgerSide(type) {
  if (HIGH_PIVOT_TYPES.has(type)) {
    return 'top';
  }

  if (LOW_PIVOT_TYPES.has(type)) {
    return 'bottom';
  }

  return null;
}

function candidateOfficialLedgerPoint(point, stage = 'final') {
  const cloned = cloneFlowPoint(point);
  const side = candidateOfficialPivotLedgerSide(cloned?.type);

  if (!cloned || !side || !cloned.at || cloned.price === null || cloned.price === undefined) {
    return null;
  }

  return {
    at: cloned.at,
    confirmedAt: cloned.confirmedAt ?? null,
    field: cloned.field ?? (side === 'top' ? 'high' : 'low'),
    locatedAt: cloned.locatedAt ?? null,
    metadata: {
      eventType: cloned.eventType ?? null,
      promotionSource: cloned.promotionSource ?? null,
      promotionReason: cloned.promotionReason ?? null,
      scope: cloned.scope ?? (stage === 'internal' ? 'internal' : 'main'),
      structureState: cloned.structureState ?? null,
    },
    price: roundPrice(cloned.price),
    promotedAt: cloned.promotedAt ?? null,
    side,
    stage,
    type: cloned.type,
  };
}

function candidateOfficialAppendLedgerPoint(state, point, stage = 'final') {
  const ledgerPoint = candidateOfficialLedgerPoint(point, stage);

  if (!ledgerPoint) {
    return;
  }

  const ledger = Array.isArray(state.pivotLedger) ? state.pivotLedger : [];
  const key = [
    ledgerPoint.type,
    ledgerPoint.price,
    ledgerPoint.at,
    ledgerPoint.confirmedAt,
    ledgerPoint.promotedAt,
    ledgerPoint.stage,
  ].join('|');
  const exists = ledger.some((entry) => {
    const entryKey = [
      entry.type,
      entry.price,
      entry.at,
      entry.confirmedAt,
      entry.promotedAt,
      entry.stage,
    ].join('|');

    return entryKey === key;
  });

  if (!exists) {
    ledger.push(ledgerPoint);
  }

  state.pivotLedger = ledger;
}

function candidateOfficialBuildPivotLedger(state) {
  const ledger = Array.isArray(state.pivotLedger) ? state.pivotLedger.map((point) => ({ ...point })) : [];
  const currentCandidate = candidateOfficialLedgerPoint(state.currentCandidate, 'candidate');

  if (currentCandidate) {
    const hasCurrent = ledger.some(
      (point) =>
        point.type === currentCandidate.type &&
        point.price === currentCandidate.price &&
        point.at === currentCandidate.at &&
        point.stage === currentCandidate.stage,
    );

    if (!hasCurrent) {
      ledger.push(currentCandidate);
    }
  }

  return ledger
    .filter((point) => candidateOfficialPivotLedgerSide(point.type))
    .sort((left, right) => {
      const leftTime = Date.parse(left.promotedAt ?? left.confirmedAt ?? left.locatedAt ?? left.at ?? 0);
      const rightTime = Date.parse(right.promotedAt ?? right.confirmedAt ?? right.locatedAt ?? right.at ?? 0);

      if (leftTime !== rightTime) {
        return leftTime - rightTime;
      }

      return Date.parse(left.at ?? 0) - Date.parse(right.at ?? 0);
    })
    .map((point, index, points) => ({
      ...point,
      nextSequenceIndex: index < points.length - 1 ? index + 1 : null,
      previousSequenceIndex: index > 0 ? index - 1 : null,
      sequenceIndex: index,
    }));
}

function candidateOfficialPointFromExtreme(type, extreme, locatedCandle, stage = 'candidate') {
  if (!extreme || !locatedCandle) {
    return null;
  }

  return withFlowPointStage(
    canonicalTrendPoint(type, extreme.price, extreme.at, locatedCandle.endAt),
    stage,
    {
      field: extreme.field,
      locatedAt: locatedCandle.startAt,
    },
  );
}

function candidateOfficialExtremeFromCandle(candle, side) {
  if (side === 'bottom') {
    return {
      at: candle.startAt,
      field: 'low',
      price: roundPrice(candle.low),
      side,
    };
  }

  if (side === 'top') {
    return {
      at: candle.startAt,
      field: 'high',
      price: roundPrice(candle.high),
      side,
    };
  }

  return null;
}

function candidateOfficialUpdateExtreme(state, candle) {
  if (!state.seeking) {
    return;
  }

  if (state.seeking === 'bottom') {
    if (!state.activeExtreme || candle.low < state.activeExtreme.price) {
      state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'bottom');
    }
    return;
  }

  if (!state.activeExtreme || candle.high > state.activeExtreme.price) {
    state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'top');
  }
}

function candidateOfficialUpdateCurrentCandidateExtreme(state, candle) {
  const candidate = state.currentCandidate;

  if (!candidate) {
    return;
  }

  if (state.seeking === 'bottom' && candidate.type === 'HH' && candle.high > candidate.price) {
    state.currentCandidate = {
      ...cloneFlowPoint(candidate),
      at: candle.startAt,
      field: 'high',
      price: roundPrice(candle.high),
    };
    candidateOfficialTrackTypedPivot(state, state.currentCandidate);
    return;
  }

  if (state.seeking === 'top' && candidate.type === 'LL' && candle.low < candidate.price) {
    state.currentCandidate = {
      ...cloneFlowPoint(candidate),
      at: candle.startAt,
      field: 'low',
      price: roundPrice(candle.low),
    };
    candidateOfficialTrackTypedPivot(state, state.currentCandidate);
  }
}

function candidateOfficialLatestBottomReference(state) {
  if (state.officialBottom) {
    return state.officialBottom;
  }

  return LOW_PIVOT_TYPES.has(state.currentCandidate?.type) ? state.currentCandidate : null;
}

function candidateOfficialLatestTopReference(state) {
  if (state.officialTop) {
    return state.officialTop;
  }

  return HIGH_PIVOT_TYPES.has(state.currentCandidate?.type) ? state.currentCandidate : null;
}

function candidateOfficialClassifyBottom(state, price) {
  const reference = candidateOfficialLatestBottomReference(state);

  if (!reference) {
    return state.trend === 'bearish' ? 'LL' : 'HL';
  }

  return price > reference.price ? 'HL' : 'LL';
}

function candidateOfficialClassifyTop(state, price) {
  const reference = candidateOfficialLatestTopReference(state);

  if (!reference) {
    return state.trend === 'bearish' ? 'LH' : 'HH';
  }

  return price > reference.price ? 'HH' : 'LH';
}

function candidateOfficialPushHistory(state, point) {
  if (!point) {
    return;
  }

  if (['HH', 'HL', 'LH', 'LL'].includes(point.type)) {
    state.pivotsByType = {
      ...(state.pivotsByType ?? {}),
      [point.type]: cloneFlowPoint(point),
    };
  }

  state.lastOfficialPivot = cloneFlowPoint(point);

  if (state.recordPivotHistory) {
    state.pivotHistory = appendPivotHistoryPoint(state.pivotHistory, state.lastOfficialPivot);
  }
}

function candidateOfficialTrackTypedPivot(state, point) {
  if (!point || !['HH', 'HL', 'LH', 'LL'].includes(point.type)) {
    return;
  }

  state.pivotsByType = {
    ...(state.pivotsByType ?? {}),
    [point.type]: cloneFlowPoint(point),
  };
}

function candidateOfficialStructuralReferenceToPreserve(state, promoted) {
  if (!promoted) {
    return null;
  }

  if (
    promoted.type === 'LH' &&
    state.characterChange?.direction !== 'down' &&
    state.officialTop?.type === 'LH' &&
    state.officialBottom?.type === 'HL' &&
    promoted.price < state.officialTop.price
  ) {
    return state.officialTop;
  }

  if (
    promoted.type === 'HL' &&
    state.characterChange?.direction !== 'up' &&
    state.officialBottom?.type === 'HL' &&
    state.officialTop?.type === 'LH' &&
    promoted.price > state.officialBottom.price
  ) {
    return state.officialBottom;
  }

  return null;
}

function candidateOfficialDecoratePoint(point, metadata = {}) {
  return point
    ? {
        ...cloneFlowPoint(point),
        ...metadata,
      }
    : null;
}

function candidateOfficialMainScopedPoint(point) {
  const cloned = cloneFlowPoint(point);

  return cloned && cloned.scope !== 'internal' ? cloned : null;
}

function candidateOfficialProtectedBreakReference(state, side) {
  if (side === 'top') {
    return candidateOfficialMainScopedPoint(state.protectedTop) ?? candidateOfficialMainScopedPoint(state.officialTop);
  }

  if (side === 'bottom') {
    return candidateOfficialMainScopedPoint(state.protectedBottom) ?? candidateOfficialMainScopedPoint(state.officialBottom);
  }

  return null;
}

function candidateOfficialSetProtectedMainCandidate(state, side, point) {
  const cloned = cloneFlowPoint(point);

  if (!cloned) {
    return null;
  }

  if (side === 'top') {
    const protectedTop = candidateOfficialDecoratePoint(cloned, {
      eventType: 'main_trend_higher_high_candidate',
      promotionReason: 'main_structure_breakout_reference_pending_hl_confirmation',
      scope: 'main',
      structureState: state.trend,
    });
    const current = candidateOfficialMainScopedPoint(state.protectedTop);

    if (!current || protectedTop.price > current.price || pointSortTime(protectedTop) >= pointSortTime(current)) {
      state.protectedTop = protectedTop;
    }

    return state.protectedTop;
  }

  if (side === 'bottom') {
    const protectedBottom = candidateOfficialDecoratePoint(cloned, {
      eventType: 'main_trend_lower_low_candidate',
      promotionReason: 'main_structure_breakdown_reference_pending_lh_confirmation',
      scope: 'main',
      structureState: state.trend,
    });
    const current = candidateOfficialMainScopedPoint(state.protectedBottom);

    if (!current || protectedBottom.price < current.price || pointSortTime(protectedBottom) >= pointSortTime(current)) {
      state.protectedBottom = protectedBottom;
    }

    return state.protectedBottom;
  }

  return null;
}

function candidateOfficialStructuralEvent(state, eventType, point, candle, events, reason, metadata = {}) {
  const structuralEvent = {
    at: candle.endAt,
    eventType,
    pivot: cloneFlowPoint(point),
    reason,
    trend: state.trend,
    ...metadata,
  };

  state.structuralEvents = [...(Array.isArray(state.structuralEvents) ? state.structuralEvents : []), structuralEvent].slice(-12);

  canonicalTrendPushEvent(
    events,
    candle,
    eventType === 'choch_against_main_structure' ? 'character_change' : 'state_changed',
    point,
    state.trend,
    reason,
    {
      ...metadata,
      reasonKey: eventType,
      structuralEvent,
    },
  );
}

function candidateOfficialTrackInternalPivot(state, point, candle, events) {
  if (!point) {
    return;
  }

  const internalPoint = candidateOfficialDecoratePoint(point, {
    eventType:
      (state.trend === 'bearish' && point.type === 'HH') ||
      (state.trend === 'bullish' && point.type === 'LL')
        ? 'internal_pivot_against_trend'
        : 'internal_pivot_with_trend',
    promotionReason: 'local_pivot_completed_without_main_structure_confirmation',
    scope: 'internal',
    structureState: state.trend,
  });

  if (HIGH_PIVOT_TYPES.has(internalPoint.type)) {
    const current = state.localFlow?.internalTop;
    if (!current || internalPoint.price >= current.price || pointSortTime(internalPoint) >= pointSortTime(current)) {
      state.localFlow.internalTop = cloneFlowPoint(internalPoint);
    }
  } else if (LOW_PIVOT_TYPES.has(internalPoint.type)) {
    const current = state.localFlow?.internalBottom;
    if (!current || internalPoint.price <= current.price || pointSortTime(internalPoint) >= pointSortTime(current)) {
      state.localFlow.internalBottom = cloneFlowPoint(internalPoint);
    }
  }

  state.localFlow.internalSwing = cloneFlowPoint(internalPoint);
  candidateOfficialTrackTypedPivot(state, internalPoint);
  candidateOfficialAppendLedgerPoint(state, internalPoint, 'internal');

  if (state.trend === 'bullish' || state.trend === 'bearish') {
    candidateOfficialStructuralEvent(
      state,
      internalPoint.eventType,
      internalPoint,
      candle,
      events,
      internalPoint.eventType === 'internal_pivot_against_trend'
        ? 'Pivo interno contra a estrutura principal confirmado sem alterar a tendencia oficial.'
        : 'Pivo interno a favor da estrutura principal confirmado sem alterar a referencia oficial.',
      {
        scope: 'internal',
      },
    );
  }
}

function candidateOfficialRegisterLocatedInternalCandidate(state, nextCandidate, candle, events, side) {
  if (!nextCandidate || (state.trend !== 'bullish' && state.trend !== 'bearish')) {
    state.localFlow.activePivot = cloneFlowPoint(nextCandidate);
    return;
  }

  const previousInternalTop = cloneFlowPoint(state.localFlow?.internalTop);
  const previousInternalBottom = cloneFlowPoint(state.localFlow?.internalBottom);
  const isAgainstTrend =
    (state.trend === 'bearish' &&
      side === 'top' &&
      previousInternalTop &&
      nextCandidate.price > previousInternalTop.price) ||
    (state.trend === 'bullish' &&
      side === 'bottom' &&
      previousInternalBottom &&
      nextCandidate.price < previousInternalBottom.price);
  const eventType = isAgainstTrend ? 'internal_pivot_against_trend' : 'internal_pivot_with_trend';
  const internalCandidate = candidateOfficialDecoratePoint(nextCandidate, {
    eventType,
    promotionReason: 'local_candidate_located_inside_main_structure',
    scope: 'internal',
    structureState: state.trend,
  });

  state.localFlow.activePivot = cloneFlowPoint(internalCandidate);

  if (side === 'top') {
    state.localFlow.internalTop = cloneFlowPoint(internalCandidate);
    if (state.trend === 'bullish') {
      candidateOfficialSetProtectedMainCandidate(state, 'top', internalCandidate);
    }
  } else if (side === 'bottom') {
    state.localFlow.internalBottom = cloneFlowPoint(internalCandidate);
    if (state.trend === 'bearish') {
      candidateOfficialSetProtectedMainCandidate(state, 'bottom', internalCandidate);
    }
  }

  if (isAgainstTrend) {
    state.localFlow.internalSwing = cloneFlowPoint(internalCandidate);
    candidateOfficialStructuralEvent(
      state,
      eventType,
      internalCandidate,
      candle,
      events,
      'Pivo interno contra a estrutura principal confirmado dentro da movimentacao interna.',
      {
        previousInternalBottom,
        previousInternalTop,
        scope: 'internal',
      },
    );
  }
}

function candidateOfficialPointsSince(state, reference, side) {
  const referenceTime = Date.parse(reference?.at ?? reference?.confirmedAt ?? 0);
  const rawPoints = [
    ...(Array.isArray(state.pivotLedger) ? state.pivotLedger : []),
    state.currentCandidate,
    state.localFlow?.internalTop,
    state.localFlow?.internalBottom,
    state.reversalCandidate?.top,
    state.reversalCandidate?.bottom,
  ];

  return rawPoints
    .map(cloneFlowPoint)
    .filter((point) => {
      if (!point?.at || point.price === null || point.price === undefined) {
        return false;
      }

      if (side === 'top' && !HIGH_PIVOT_TYPES.has(point.type)) {
        return false;
      }

      if (side === 'bottom' && !LOW_PIVOT_TYPES.has(point.type)) {
        return false;
      }

      return Date.parse(point.at) > referenceTime;
    });
}

function candidateOfficialHighestTopSince(state, reference) {
  return candidateOfficialPointsSince(state, reference, 'top').sort((left, right) => {
    if (right.price !== left.price) {
      return right.price - left.price;
    }

    return pointSortTime(right) - pointSortTime(left);
  })[0] ?? null;
}

function candidateOfficialLowestBottomSince(state, reference) {
  return candidateOfficialPointsSince(state, reference, 'bottom').sort((left, right) => {
    if (left.price !== right.price) {
      return left.price - right.price;
    }

    return pointSortTime(right) - pointSortTime(left);
  })[0] ?? null;
}

function candidateOfficialMainFinalPoint(point, candle, eventType, promotionReason, structureState) {
  return candidateOfficialDecoratePoint(canonicalTrendFinalPoint(point, candle.endAt), {
    eventType,
    promotionReason,
    scope: 'main',
    structureState,
  });
}

function candidateOfficialSetMainTop(state, point, candle, eventType, promotionReason) {
  const finalPoint = candidateOfficialMainFinalPoint(point, candle, eventType, promotionReason, state.trend);

  if (!finalPoint) {
    return null;
  }

  state.officialTop = finalPoint;
  state.protectedTop = null;
  candidateOfficialPushHistory(state, finalPoint);
  candidateOfficialAppendLedgerPoint(state, finalPoint, 'final');

  return finalPoint;
}

function candidateOfficialSetMainBottom(state, point, candle, eventType, promotionReason) {
  const finalPoint = candidateOfficialMainFinalPoint(point, candle, eventType, promotionReason, state.trend);

  if (!finalPoint) {
    return null;
  }

  state.officialBottom = finalPoint;
  state.protectedBottom = null;
  candidateOfficialPushHistory(state, finalPoint);
  candidateOfficialAppendLedgerPoint(state, finalPoint, 'final');

  return finalPoint;
}

function candidateOfficialContinuationLowPoint(state, candle) {
  const activeLow =
    state.activeExtreme?.side === 'bottom' && state.activeExtreme.price <= candle.low
      ? state.activeExtreme
      : candidateOfficialExtremeFromCandle(candle, 'bottom');

  return candidateOfficialPointFromExtreme('LL', activeLow, candle, 'candidate');
}

function candidateOfficialContinuationHighPoint(state, candle) {
  const activeHigh =
    state.activeExtreme?.side === 'top' && state.activeExtreme.price >= candle.high
      ? state.activeExtreme
      : candidateOfficialExtremeFromCandle(candle, 'top');

  return candidateOfficialPointFromExtreme('HH', activeHigh, candle, 'candidate');
}

function candidateOfficialResolveMainStructure(state, candle, events) {
  const bearishBreakReference = candidateOfficialProtectedBreakReference(state, 'bottom');
  if (state.trend === 'bearish' && bearishBreakReference && candle.close < bearishBreakReference.price) {
    const previousBottom = cloneFlowPoint(bearishBreakReference);
    const bestTop = candidateOfficialHighestTopSince(state, previousBottom);
    const nextBottom = candidateOfficialContinuationLowPoint(state, candle);

    if (!bestTop) {
      if (!state.officialBottom || nextBottom.price < state.officialBottom.price) {
        state.officialBottom = candidateOfficialDecoratePoint(nextBottom, {
          eventType: 'main_trend_lower_low_candidate',
          promotionReason: 'bearish_low_renewed_before_new_main_top',
          scope: 'main',
          structureState: 'bearish',
        });
        candidateOfficialTrackTypedPivot(state, state.officialBottom);
      }
      return false;
    }

    const failedChoch =
      state.characterChange?.direction === 'up' ||
      (state.officialTop && bestTop.price > state.officialTop.price);
    const topType = state.officialTop && bestTop.price > state.officialTop.price ? 'HH' : 'LH';
    const officialTop = candidateOfficialSetMainTop(
      state,
      {
        ...bestTop,
        type: topType,
      },
      candle,
      failedChoch ? 'choch_failed_continuation' : 'main_trend_continuation_confirmed',
      failedChoch
        ? 'choch_failed_after_protected_low_break'
        : 'bearish_continuation_after_protected_low_break',
    );

    state.officialBottom = candidateOfficialDecoratePoint(nextBottom, {
      eventType: 'main_trend_lower_low_candidate',
      promotionReason: 'protected_low_broken_after_main_top_confirmation',
      scope: 'main',
      structureState: 'bearish',
    });
    candidateOfficialTrackTypedPivot(state, state.officialBottom);
    state.protectedBottom = null;
    state.characterChange = null;
    state.reversalCandidate = null;
    state.currentCandidate = cloneFlowPoint(state.officialBottom);
    state.seeking = 'top';
    state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'top');
    state.trend = 'bearish';

    candidateOfficialStructuralEvent(
      state,
      failedChoch ? 'choch_failed_continuation' : 'main_trend_continuation_confirmed',
      officialTop,
      candle,
      events,
      failedChoch
        ? 'ChoCH de alta falhou; a baixa retomou ao perder o fundo estrutural protegido.'
        : 'Continuidade de baixa confirmada por fechamento abaixo do fundo estrutural protegido.',
      {
        protectedReference: previousBottom,
        newExtreme: cloneFlowPoint(state.officialBottom),
        scope: 'main',
      },
    );

    return true;
  }

  const bullishBreakReference = candidateOfficialProtectedBreakReference(state, 'top');
  if (state.trend === 'bullish' && bullishBreakReference && candle.close > bullishBreakReference.price) {
    const previousTop = cloneFlowPoint(bullishBreakReference);
    const bestBottom = candidateOfficialLowestBottomSince(state, previousTop);
    const nextTop = candidateOfficialContinuationHighPoint(state, candle);

    if (!bestBottom) {
      if (!state.officialTop || nextTop.price > state.officialTop.price) {
        state.officialTop = candidateOfficialDecoratePoint(nextTop, {
          eventType: 'main_trend_higher_high_candidate',
          promotionReason: 'bullish_high_renewed_before_new_main_bottom',
          scope: 'main',
          structureState: 'bullish',
        });
        candidateOfficialTrackTypedPivot(state, state.officialTop);
      }
      return false;
    }

    const failedChoch =
      state.characterChange?.direction === 'down' ||
      (state.officialBottom && bestBottom.price < state.officialBottom.price);
    const bottomType = state.officialBottom && bestBottom.price < state.officialBottom.price ? 'LL' : 'HL';
    const officialBottom = candidateOfficialSetMainBottom(
      state,
      {
        ...bestBottom,
        type: bottomType,
      },
      candle,
      failedChoch ? 'choch_failed_continuation' : 'main_trend_continuation_confirmed',
      failedChoch
        ? 'choch_failed_after_protected_high_break'
        : 'bullish_continuation_after_protected_high_break',
    );

    state.officialTop = candidateOfficialDecoratePoint(nextTop, {
      eventType: 'main_trend_higher_high_candidate',
      promotionReason: 'protected_high_broken_after_main_bottom_confirmation',
      scope: 'main',
      structureState: 'bullish',
    });
    candidateOfficialTrackTypedPivot(state, state.officialTop);
    state.protectedTop = null;
    state.characterChange = null;
    state.reversalCandidate = null;
    state.currentCandidate = cloneFlowPoint(state.officialTop);
    state.seeking = 'bottom';
    state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'bottom');
    state.trend = 'bullish';

    candidateOfficialStructuralEvent(
      state,
      failedChoch ? 'choch_failed_continuation' : 'main_trend_continuation_confirmed',
      officialBottom,
      candle,
      events,
      failedChoch
        ? 'ChoCH de baixa falhou; a alta retomou ao romper o topo estrutural protegido.'
        : 'Continuidade de alta confirmada por fechamento acima do topo estrutural protegido.',
      {
        protectedReference: previousTop,
        newExtreme: cloneFlowPoint(state.officialTop),
        scope: 'main',
      },
    );

    return true;
  }

  return false;
}

function candidateOfficialEnsureReversalCandidate(state, direction, candle, referencePivot) {
  const previous = state.reversalCandidate?.direction === direction ? state.reversalCandidate : null;

  if (direction === 'up') {
    const top = previous?.bottom
      ? previous.top
      : previous?.top && previous.top.price >= candle.high
      ? previous.top
      : candidateOfficialPointFromExtreme('HH', candidateOfficialExtremeFromCandle(candle, 'top'), candle, 'candidate');

    state.reversalCandidate = {
      bottom: cloneFlowPoint(previous?.bottom),
      direction,
      referenceBottom: cloneFlowPoint(state.officialBottom),
      referenceTop: cloneFlowPoint(referencePivot ?? state.officialTop),
      status: 'choch_active',
      top: cloneFlowPoint(top),
    };
  } else if (direction === 'down') {
    const bottom = previous?.top
      ? previous.bottom
      : previous?.bottom && previous.bottom.price <= candle.low
      ? previous.bottom
      : candidateOfficialPointFromExtreme('LL', candidateOfficialExtremeFromCandle(candle, 'bottom'), candle, 'candidate');

    state.reversalCandidate = {
      bottom: cloneFlowPoint(bottom),
      direction,
      referenceBottom: cloneFlowPoint(referencePivot ?? state.officialBottom),
      referenceTop: cloneFlowPoint(state.officialTop),
      status: 'choch_active',
      top: cloneFlowPoint(previous?.top),
    };
  }
}

function candidateOfficialTrackReversalCandidatePivot(state, point) {
  if (!point || !state.reversalCandidate) {
    return;
  }

  if (state.reversalCandidate.direction === 'up') {
    if (HIGH_PIVOT_TYPES.has(point.type) && point.price > (state.reversalCandidate.referenceTop?.price ?? -Infinity)) {
      if (!state.reversalCandidate.top || point.price >= state.reversalCandidate.top.price) {
        state.reversalCandidate.top = cloneFlowPoint(point);
      }
    }

    if (
      LOW_PIVOT_TYPES.has(point.type) &&
      state.reversalCandidate.top &&
      point.price > (state.reversalCandidate.referenceBottom?.price ?? -Infinity)
    ) {
      if (!state.reversalCandidate.bottom || point.price <= state.reversalCandidate.bottom.price) {
        state.reversalCandidate.bottom = cloneFlowPoint(point);
      }
    }
  }

  if (state.reversalCandidate.direction === 'down') {
    if (LOW_PIVOT_TYPES.has(point.type) && point.price < (state.reversalCandidate.referenceBottom?.price ?? Infinity)) {
      if (!state.reversalCandidate.bottom || point.price <= state.reversalCandidate.bottom.price) {
        state.reversalCandidate.bottom = cloneFlowPoint(point);
      }
    }

    if (
      HIGH_PIVOT_TYPES.has(point.type) &&
      state.reversalCandidate.bottom &&
      point.price < (state.reversalCandidate.referenceTop?.price ?? Infinity)
    ) {
      if (!state.reversalCandidate.top || point.price >= state.reversalCandidate.top.price) {
        state.reversalCandidate.top = cloneFlowPoint(point);
      }
    }
  }
}

function candidateOfficialResolveStructuralReversal(state, candle, events) {
  const reversal = state.reversalCandidate;

  if (
    state.trend === 'bearish' &&
    reversal?.direction === 'up' &&
    reversal.top &&
    reversal.bottom &&
    candle.close > reversal.top.price
  ) {
    const previousTrend = state.trend;
    state.trend = 'bullish';
    const officialBottom = candidateOfficialSetMainBottom(
      state,
      { ...reversal.bottom, type: 'HL' },
      candle,
      'structural_reversal_confirmed',
      'bullish_reversal_confirmed_after_hh_hl_hh',
    );
    const officialTop = candidateOfficialDecoratePoint(
      candidateOfficialContinuationHighPoint(state, candle),
      {
        eventType: 'main_trend_higher_high_candidate',
        promotionReason: 'bullish_reversal_breakout_high',
        scope: 'main',
        structureState: 'bullish',
      },
    );

    state.officialTop = officialTop;
    candidateOfficialTrackTypedPivot(state, officialTop);
    state.currentCandidate = cloneFlowPoint(officialTop);
    state.characterChange = null;
    state.reversalCandidate = null;
    state.seeking = 'bottom';
    state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'bottom');

    candidateOfficialStructuralEvent(
      state,
      'structural_reversal_confirmed',
      officialBottom,
      candle,
      events,
      'Reversao estrutural forte para alta confirmada por HH, HL e novo rompimento do HH.',
      {
        newExtreme: cloneFlowPoint(officialTop),
        previousTrend,
        scope: 'main',
      },
    );

    return true;
  }

  if (
    state.trend === 'bullish' &&
    reversal?.direction === 'down' &&
    reversal.bottom &&
    reversal.top &&
    candle.close < reversal.bottom.price
  ) {
    const previousTrend = state.trend;
    state.trend = 'bearish';
    const officialTop = candidateOfficialSetMainTop(
      state,
      { ...reversal.top, type: 'LH' },
      candle,
      'structural_reversal_confirmed',
      'bearish_reversal_confirmed_after_ll_lh_ll',
    );
    const officialBottom = candidateOfficialDecoratePoint(
      candidateOfficialContinuationLowPoint(state, candle),
      {
        eventType: 'main_trend_lower_low_candidate',
        promotionReason: 'bearish_reversal_breakout_low',
        scope: 'main',
        structureState: 'bearish',
      },
    );

    state.officialBottom = officialBottom;
    candidateOfficialTrackTypedPivot(state, officialBottom);
    state.currentCandidate = cloneFlowPoint(officialBottom);
    state.characterChange = null;
    state.reversalCandidate = null;
    state.seeking = 'top';
    state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'top');

    candidateOfficialStructuralEvent(
      state,
      'structural_reversal_confirmed',
      officialTop,
      candle,
      events,
      'Reversao estrutural forte para baixa confirmada por LL, LH e novo rompimento do LL.',
      {
        newExtreme: cloneFlowPoint(officialBottom),
        previousTrend,
        scope: 'main',
      },
    );

    return true;
  }

  return false;
}

function candidateOfficialPromoteCurrentCandidate(state, nextCandidate, candle, events) {
  if (!state.currentCandidate || !nextCandidate) {
    state.currentCandidate = cloneFlowPoint(nextCandidate);
    candidateOfficialTrackTypedPivot(state, state.currentCandidate);
    return;
  }

  const promoted = canonicalTrendFinalPoint(
    {
      ...cloneFlowPoint(state.currentCandidate),
      promotionSource: `${nextCandidate.type}:${nextCandidate.price}`,
      promotedAt: candle.endAt,
    },
    candle.endAt,
  );

  if (!state.officialTop || !state.officialBottom || state.trend === 'neutral') {
    if (HIGH_PIVOT_TYPES.has(promoted.type)) {
      candidateOfficialSetMainTop(
        state,
        promoted,
        candle,
        'bootstrap_main_reference_confirmed',
        'bootstrap_until_main_structure_exists',
      );
    } else if (LOW_PIVOT_TYPES.has(promoted.type)) {
      candidateOfficialSetMainBottom(
        state,
        promoted,
        candle,
        'bootstrap_main_reference_confirmed',
        'bootstrap_until_main_structure_exists',
      );
    }

    state.currentCandidate = cloneFlowPoint(nextCandidate);
    candidateOfficialTrackTypedPivot(state, state.currentCandidate);
    state.trend = candidateOfficialDeriveTrend(state);
    return;
  }

  candidateOfficialTrackInternalPivot(state, promoted, candle, events);
  candidateOfficialTrackReversalCandidatePivot(state, promoted);
  state.currentCandidate = cloneFlowPoint(nextCandidate);
  candidateOfficialTrackTypedPivot(state, state.currentCandidate);
  candidateOfficialTrackReversalCandidatePivot(state, state.currentCandidate);
}

function candidateOfficialDeriveTrend(state) {
  const top = state.officialTop;
  const bottom = state.officialBottom;

  if (state.trend === 'bullish' || state.trend === 'bearish') {
    return state.trend;
  }

  if (top?.type === 'HH' && bottom?.type === 'HL') {
    return 'bullish';
  }

  if (top?.type === 'LH' && bottom?.type === 'LL') {
    return 'bearish';
  }

  return 'neutral';
}

function candidateOfficialCheckSweepAndChoch(state, candle, events) {
  const trend = candidateOfficialDeriveTrend(state);
  const bottomReference = state.officialBottom;
  const topReference = state.officialTop;

  if (trend === 'bullish' && bottomReference && LOW_PIVOT_TYPES.has(bottomReference.type)) {
    if (candle.low < bottomReference.price && candle.close >= bottomReference.price) {
      state.sweep = primaryRangeSweepFromCandle('down', candle, bottomReference);
    }

    if (candle.close < bottomReference.price) {
      state.characterChange = canonicalTrendCharacterChange('down', bottomReference, candle, 'bullish');
      candidateOfficialEnsureReversalCandidate(state, 'down', candle, bottomReference);
      state.structuralEvents = [
        ...(Array.isArray(state.structuralEvents) ? state.structuralEvents : []),
        {
          at: candle.endAt,
          direction: 'down',
          eventType: 'choch_against_main_structure',
          pivot: cloneFlowPoint(bottomReference),
          reason: state.characterChange.reason,
          trend,
        },
      ].slice(-12);
      canonicalTrendPushEvent(
        events,
        candle,
        'character_change',
        bottomReference,
        trend,
        state.characterChange.reason,
        {
          characterChange: state.characterChange,
          reasonKey: 'choch_against_main_structure',
          structuralEventType: 'choch_against_main_structure',
        },
      );
    }
  }

  if (trend === 'bearish' && topReference && HIGH_PIVOT_TYPES.has(topReference.type)) {
    if (candle.high > topReference.price && candle.close <= topReference.price) {
      state.sweep = primaryRangeSweepFromCandle('up', candle, topReference);
    }

    if (candle.close > topReference.price) {
      state.characterChange = canonicalTrendCharacterChange('up', topReference, candle, 'bearish');
      candidateOfficialEnsureReversalCandidate(state, 'up', candle, topReference);
      state.structuralEvents = [
        ...(Array.isArray(state.structuralEvents) ? state.structuralEvents : []),
        {
          at: candle.endAt,
          direction: 'up',
          eventType: 'choch_against_main_structure',
          pivot: cloneFlowPoint(topReference),
          reason: state.characterChange.reason,
          trend,
        },
      ].slice(-12);
      canonicalTrendPushEvent(
        events,
        candle,
        'character_change',
        topReference,
        trend,
        state.characterChange.reason,
        {
          characterChange: state.characterChange,
          reasonKey: 'choch_against_main_structure',
          structuralEventType: 'choch_against_main_structure',
        },
      );
    }
  }
}

function candidateOfficialMode(state) {
  if (state.characterChange?.direction === 'down') {
    return 'choch_down_active';
  }

  if (state.characterChange?.direction === 'up') {
    return 'choch_up_active';
  }

  if (state.trend === 'bullish') {
    return state.seeking === 'top' ? 'bullish_tracking_hh' : 'bullish_tracking_provisional_hl';
  }

  if (state.trend === 'bearish') {
    return state.seeking === 'bottom' ? 'bearish_tracking_ll' : 'bearish_tracking_provisional_lh';
  }

  return 'neutral_bootstrap';
}

function candidateOfficialProcessBottomCandidate(state, candle, events, options = {}) {
  const candidateType = candidateOfficialClassifyBottom(state, state.activeExtreme?.price);
  const nextCandidate = candidateOfficialPointFromExtreme(candidateType, state.activeExtreme, candle);

  if (!nextCandidate) {
    return;
  }

  candidateOfficialPromoteCurrentCandidate(state, nextCandidate, candle, events);
  canonicalTrendPushEvent(
    events,
    candle,
    'state_changed',
    nextCandidate,
    state.trend,
    options.reason ?? 'Fundo candidato localizado por fechamento acima da maxima do candle anterior.',
    {
      reasonKey: options.reasonKey ?? 'candidate_located',
      candidateSide: 'bottom',
      ...(options.metadata ?? {}),
    },
  );
  candidateOfficialRegisterLocatedInternalCandidate(state, nextCandidate, candle, events, 'bottom');
  state.seeking = 'top';
  state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'top');
  state.requiresCorrectionBeforeBottomCandidate = false;
  state.trend = candidateOfficialDeriveTrend(state);
  return nextCandidate;
}

function candidateOfficialProcessTopCandidate(state, candle, events, options = {}) {
  const candidateType = candidateOfficialClassifyTop(state, state.activeExtreme?.price);
  const nextCandidate = candidateOfficialPointFromExtreme(candidateType, state.activeExtreme, candle);

  if (!nextCandidate) {
    return;
  }

  candidateOfficialPromoteCurrentCandidate(state, nextCandidate, candle, events);
  canonicalTrendPushEvent(
    events,
    candle,
    'state_changed',
    nextCandidate,
    state.trend,
    options.reason ?? 'Topo candidato localizado por fechamento abaixo da minima do candle anterior.',
    {
      reasonKey: options.reasonKey ?? 'candidate_located',
      candidateSide: 'top',
      ...(options.metadata ?? {}),
    },
  );
  candidateOfficialRegisterLocatedInternalCandidate(state, nextCandidate, candle, events, 'top');
  state.seeking = 'bottom';
  state.activeExtreme = candidateOfficialExtremeFromCandle(candle, 'bottom');
  state.requiredTopCorrectionBreakPrice = null;
  state.requiresCorrectionBeforeTopCandidate = false;
  state.trend = candidateOfficialDeriveTrend(state);
  return nextCandidate;
}

function candidateOfficialProcessStructuralBreakoutCandidate(state, candle, events) {
  return false;
}

function candidateOfficialAdvanceState(state, previous, current, events) {
  state.lastProcessedCandleAt = current.endAt;
  state.sweep = null;

  if (!state.seeking) {
    if (current.close > previous.high) {
      state.seeking = 'bottom';
      state.activeExtreme = candidateOfficialExtremeFromCandle(
        previous.low <= current.low ? previous : current,
        'bottom',
      );
      candidateOfficialProcessBottomCandidate(state, current, events);
      return;
    }

    if (current.close < previous.low) {
      state.seeking = 'top';
      state.activeExtreme = candidateOfficialExtremeFromCandle(
        previous.high >= current.high ? previous : current,
        'top',
      );
      candidateOfficialProcessTopCandidate(state, current, events);
    }

    return;
  }

  candidateOfficialUpdateCurrentCandidateExtreme(state, current);

  const topCandidateWasCorrectionBlocked =
    state.seeking === 'top' && state.requiresCorrectionBeforeTopCandidate;
  const bottomCandidateWasCorrectionBlocked =
    state.seeking === 'bottom' && state.requiresCorrectionBeforeBottomCandidate;
  let topCandidateCorrectionReleased = false;
  let bottomCandidateCorrectionReleased = false;

  if (topCandidateWasCorrectionBlocked) {
    if (current.close > previous.high) {
      state.requiresCorrectionBeforeTopCandidate = false;
      state.activeExtreme = candidateOfficialExtremeFromCandle(current, 'top');
      topCandidateCorrectionReleased = true;
    }
  } else if (bottomCandidateWasCorrectionBlocked) {
    if (current.close < previous.low) {
      state.requiresCorrectionBeforeBottomCandidate = false;
      state.activeExtreme = candidateOfficialExtremeFromCandle(current, 'bottom');
      bottomCandidateCorrectionReleased = true;
    }
  } else {
    candidateOfficialUpdateExtreme(state, current);
  }

  candidateOfficialCheckSweepAndChoch(state, current, events);
  const characterChangeAlreadyDetected = Boolean(state.characterChange);

  if (candidateOfficialResolveMainStructure(state, current, events)) {
    return;
  }

  if (candidateOfficialResolveStructuralReversal(state, current, events)) {
    return;
  }

  if (candidateOfficialProcessStructuralBreakoutCandidate(state, current, events)) {
    return;
  }

  if (state.seeking === 'bottom' && current.close > previous.high) {
    if (bottomCandidateWasCorrectionBlocked && !bottomCandidateCorrectionReleased) {
      return;
    }
    candidateOfficialProcessBottomCandidate(state, current, events);
    if (!characterChangeAlreadyDetected) {
      candidateOfficialCheckSweepAndChoch(state, current, events);
      if (candidateOfficialResolveMainStructure(state, current, events)) {
        return;
      }
      if (candidateOfficialResolveStructuralReversal(state, current, events)) {
        return;
      }
      if (candidateOfficialProcessStructuralBreakoutCandidate(state, current, events)) {
        return;
      }
    }
    return;
  }

  if (state.seeking === 'top' && current.close < previous.low) {
    if (topCandidateWasCorrectionBlocked && !topCandidateCorrectionReleased) {
      return;
    }
    candidateOfficialProcessTopCandidate(state, current, events);
    if (!characterChangeAlreadyDetected) {
      candidateOfficialCheckSweepAndChoch(state, current, events);
      if (candidateOfficialResolveMainStructure(state, current, events)) {
        return;
      }
      if (candidateOfficialResolveStructuralReversal(state, current, events)) {
        return;
      }
      if (candidateOfficialProcessStructuralBreakoutCandidate(state, current, events)) {
        return;
      }
    }
  }
}

function candidateOfficialStateFromPrimaryRange(range) {
  if (!isCanonicalPrimaryRange(range)) {
    return null;
  }

  const officialOnly = (point) => {
    const cloned = cloneFlowPoint(point);

    return cloned && cloned.stage !== 'candidate' && cloned.stage !== 'preconfirmed' && cloned.stage !== 'provisional'
      ? cloned
      : null;
  };
  const cycle = range.candidateCycle ?? {};
  const currentCandidate =
    cloneFlowPoint(cycle.currentCandidate) ??
    cloneFlowPoint(range.currentCandidate) ??
    cloneFlowPoint(range.activeSwing?.stage === 'candidate' ? range.activeSwing : null);
  const officialTop =
    officialOnly(cycle.officialTop) ??
    officialOnly(range.officialTop) ??
    officialOnly(range.confirmedHH) ??
    officialOnly(range.confirmedLH) ??
    officialOnly(HIGH_PIVOT_TYPES.has(range.top?.type) ? range.top : null);
  const officialBottom =
    officialOnly(cycle.officialBottom) ??
    officialOnly(range.officialBottom) ??
    officialOnly(range.confirmedHL) ??
    officialOnly(range.confirmedLL) ??
    officialOnly(LOW_PIVOT_TYPES.has(range.bottom?.type) ? range.bottom : null);
  const activeExtreme = cycle.activeExtreme
    ? { ...cycle.activeExtreme, price: roundPrice(cycle.activeExtreme.price) }
    : range.activeSwing && LOW_PIVOT_TYPES.has(range.activeSwing.type)
      ? {
          at: range.activeSwing.at,
          field: 'low',
          price: roundPrice(range.activeSwing.price),
          side: 'bottom',
        }
      : range.activeSwing && HIGH_PIVOT_TYPES.has(range.activeSwing.type)
        ? {
            at: range.activeSwing.at,
            field: 'high',
            price: roundPrice(range.activeSwing.price),
            side: 'top',
          }
        : null;
  const seeking =
    cycle.seeking ??
    range.seeking ??
    (range.movementAxis === 'seeking_bottom'
      ? 'bottom'
      : range.movementAxis === 'seeking_top'
        ? 'top'
        : null);

  const state = {
    activeExtreme,
    characterChange: cloneCharacterChange(range.characterChange),
    currentCandidate,
    lastOfficialPivot: cloneFlowPoint(cycle.lastOfficialPivot ?? range.lastOfficialPivot ?? range.lastPivot),
    lastProcessedCandleAt: range.lastProcessedCandleAt ?? null,
    localFlow: {
      activePivot: cloneFlowPoint(range.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(range.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(range.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(range.localFlow?.internalTop),
    },
    officialBottom,
    officialTop,
    protectedBottom:
      candidateOfficialMainScopedPoint(cycle.protectedBottom) ?? candidateOfficialMainScopedPoint(range.protectedBottom),
    protectedTop: candidateOfficialMainScopedPoint(cycle.protectedTop) ?? candidateOfficialMainScopedPoint(range.protectedTop),
    pivotsByType: {
      HH: cloneFlowPoint(cycle.pivotsByType?.HH ?? range.confirmedHH ?? (officialTop?.type === 'HH' ? officialTop : null)),
      HL: cloneFlowPoint(cycle.pivotsByType?.HL ?? range.confirmedHL ?? (officialBottom?.type === 'HL' ? officialBottom : null)),
      LH: cloneFlowPoint(cycle.pivotsByType?.LH ?? range.confirmedLH ?? (officialTop?.type === 'LH' ? officialTop : null)),
      LL: cloneFlowPoint(cycle.pivotsByType?.LL ?? range.confirmedLL ?? (officialBottom?.type === 'LL' ? officialBottom : null)),
    },
    pivotLedger: Array.isArray(range.pivotLedger) ? range.pivotLedger.map((point) => ({ ...point })) : [],
    pivotHistory: normalizePivotHistory(range.pivotHistory),
    recordPivotHistory: true,
    reversalCandidate: range.reversalCandidate
      ? {
          bottom: cloneFlowPoint(range.reversalCandidate.bottom),
          direction: range.reversalCandidate.direction ?? null,
          referenceBottom: cloneFlowPoint(range.reversalCandidate.referenceBottom),
          referenceTop: cloneFlowPoint(range.reversalCandidate.referenceTop),
          status: range.reversalCandidate.status ?? null,
          top: cloneFlowPoint(range.reversalCandidate.top),
        }
      : null,
    requiredTopCorrectionBreakPrice:
      cycle.requiredTopCorrectionBreakPrice === null || cycle.requiredTopCorrectionBreakPrice === undefined
        ? null
        : Number.isFinite(Number(cycle.requiredTopCorrectionBreakPrice))
          ? roundPrice(Number(cycle.requiredTopCorrectionBreakPrice))
          : null,
    requiresCorrectionBeforeBottomCandidate: Boolean(cycle.requiresCorrectionBeforeBottomCandidate),
    requiresCorrectionBeforeTopCandidate: Boolean(cycle.requiresCorrectionBeforeTopCandidate),
    seeking,
    structuralEvents: Array.isArray(range.structuralEvents) ? range.structuralEvents.map((event) => ({ ...event })) : [],
    sweep: clonePrimaryRangeSweep(range.sweep),
    trend: range.trend ?? 'neutral',
  };

  state.trend = candidateOfficialDeriveTrend(state);

  return state;
}

function candidateOfficialLatestPointByType(state, type) {
  const points = [
    state.pivotsByType?.[type],
    state.officialTop,
    state.officialBottom,
    state.currentCandidate,
    ...(Array.isArray(state.pivotHistory) ? state.pivotHistory : []),
  ]
    .map(cloneFlowPoint)
    .filter((point) => point?.type === type);

  return points.sort((left, right) => pointSortTime(right) - pointSortTime(left))[0] ?? null;
}

function candidateOfficialPrimaryRange(state) {
  const candidate = cloneFlowPoint(state.currentCandidate);
  const top = cloneFlowPoint(state.officialTop);
  const bottom = cloneFlowPoint(state.officialBottom);
  const lastOfficialPivot = cloneFlowPoint(state.lastOfficialPivot ?? latestConfirmedPivot(state.officialTop, state.officialBottom));
  const trend = candidateOfficialDeriveTrend(state);
  const mode = candidateOfficialMode({ ...state, trend });
  const movementAxis = state.seeking === 'top' ? 'seeking_top' : state.seeking === 'bottom' ? 'seeking_bottom' : 'inside_range';
  const activePivot = candidate ?? (
    state.activeExtreme
      ? canonicalTrendPoint(
          state.activeExtreme.side === 'top' ? 'H' : 'L',
          state.activeExtreme.price,
          state.activeExtreme.at,
        )
      : null
  );
  const pivotLedger = candidateOfficialBuildPivotLedger(state);

  return {
    activePivot: cloneFlowPoint(activePivot),
    activePrimaryPivot: cloneFlowPoint(activePivot),
    activeSwing: cloneFlowPoint(activePivot),
    anchorBottom: cloneFlowPoint(bottom),
    anchorTop: cloneFlowPoint(top),
    bottom: cloneFlowPoint(bottom),
    candidateCycle: {
      activeExtreme: state.activeExtreme
        ? {
            at: state.activeExtreme.at,
            field: state.activeExtreme.field,
            price: roundPrice(state.activeExtreme.price),
            side: state.activeExtreme.side,
          }
        : null,
      currentCandidate: cloneFlowPoint(candidate),
      lastOfficialPivot,
      officialBottom: cloneFlowPoint(state.officialBottom),
      officialTop: cloneFlowPoint(state.officialTop),
      protectedBottom: cloneFlowPoint(state.protectedBottom),
      protectedTop: cloneFlowPoint(state.protectedTop),
      pivotsByType: {
        HH: cloneFlowPoint(state.pivotsByType?.HH),
        HL: cloneFlowPoint(state.pivotsByType?.HL),
        LH: cloneFlowPoint(state.pivotsByType?.LH),
        LL: cloneFlowPoint(state.pivotsByType?.LL),
      },
      requiredTopCorrectionBreakPrice:
        state.requiredTopCorrectionBreakPrice === null || state.requiredTopCorrectionBreakPrice === undefined
          ? null
          : Number.isFinite(Number(state.requiredTopCorrectionBreakPrice))
            ? roundPrice(Number(state.requiredTopCorrectionBreakPrice))
            : null,
      requiresCorrectionBeforeBottomCandidate: Boolean(state.requiresCorrectionBeforeBottomCandidate),
      requiresCorrectionBeforeTopCandidate: Boolean(state.requiresCorrectionBeforeTopCandidate),
      seeking: state.seeking,
      version: TREND_STRUCTURE_PRIMARY_RANGE_STATE_VERSION,
    },
    characterChange: cloneCharacterChange(state.characterChange),
    confirmedHH: cloneFlowPoint(state.officialTop?.type === 'HH' ? state.officialTop : candidateOfficialLatestPointByType(state, 'HH')),
    confirmedHL: cloneFlowPoint(state.officialBottom?.type === 'HL' ? state.officialBottom : candidateOfficialLatestPointByType(state, 'HL')),
    confirmedLH: cloneFlowPoint(state.officialTop?.type === 'LH' ? state.officialTop : candidateOfficialLatestPointByType(state, 'LH')),
    confirmedLL: cloneFlowPoint(state.officialBottom?.type === 'LL' ? state.officialBottom : candidateOfficialLatestPointByType(state, 'LL')),
    currentCandidate: cloneFlowPoint(candidate),
    finalizationLevel: activePivot?.price ?? null,
    finalizationRule:
      state.seeking === 'bottom'
        ? 'close_above_previous_high'
        : state.seeking === 'top'
          ? 'close_below_previous_low'
          : null,
    lastOfficialPivot,
    lastPivot: cloneFlowPoint(lastOfficialPivot),
    lastProcessedCandleAt: state.lastProcessedCandleAt,
    localFlow: {
      activePivot: cloneFlowPoint(state.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(state.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(state.localFlow?.internalTop),
    },
    mainStructure: {
      lastStructuralEvent: Array.isArray(state.structuralEvents) ? state.structuralEvents.at(-1) ?? null : null,
      officialBottom: cloneFlowPoint(state.officialBottom),
      officialTop: cloneFlowPoint(state.officialTop),
      protectedBottom: cloneFlowPoint(state.protectedBottom ?? state.officialBottom),
      protectedTop: cloneFlowPoint(state.protectedTop ?? state.officialTop),
      state:
        state.characterChange?.direction === 'up' || state.characterChange?.direction === 'down'
          ? 'choch_active'
          : movementAxis === 'seeking_top'
            ? 'seeking_top'
            : movementAxis === 'seeking_bottom'
              ? 'seeking_bottom'
              : 'inside_range',
      stopReference:
        trend === 'bullish'
          ? cloneFlowPoint(state.officialBottom)
          : trend === 'bearish'
            ? cloneFlowPoint(state.officialTop)
            : null,
      trend,
    },
    internalStructure: {
      confirmedInternalPivot: cloneFlowPoint(state.localFlow?.internalSwing),
      direction:
        state.localFlow?.internalTop && state.localFlow?.internalBottom
          ? pointSortTime(state.localFlow.internalTop) > pointSortTime(state.localFlow.internalBottom)
            ? 'up'
            : 'down'
          : null,
      lastInternalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      lastInternalTop: cloneFlowPoint(state.localFlow?.internalTop),
      pressureAgainstMainTrend:
        state.characterChange?.direction
          ? 'strong'
          : state.localFlow?.internalSwing?.eventType === 'internal_pivot_against_trend'
          ? 'moderate'
          : 'none',
    },
    mode,
    movementAxis,
    officialBottom: cloneFlowPoint(state.officialBottom),
    officialTop: cloneFlowPoint(state.officialTop),
    pivotLedger,
    pivotHistory: normalizePivotHistory(state.pivotHistory),
    preconfirmedAt: null,
    preconfirmedPivot: null,
    preconfirmedReference: null,
    reversal: null,
    reversalCandidate: state.reversalCandidate
      ? {
          bottom: cloneFlowPoint(state.reversalCandidate.bottom),
          direction: state.reversalCandidate.direction ?? null,
          referenceBottom: cloneFlowPoint(state.reversalCandidate.referenceBottom),
          referenceTop: cloneFlowPoint(state.reversalCandidate.referenceTop),
          status: state.reversalCandidate.status ?? null,
          top: cloneFlowPoint(state.reversalCandidate.top),
        }
      : null,
    state: movementAxis === 'seeking_top' ? 'seeking_top' : movementAxis === 'seeking_bottom' ? 'seeking_bottom' : 'inside_range',
    stateVersion: TREND_STRUCTURE_PRIMARY_RANGE_STATE_VERSION,
    structuralEvents: Array.isArray(state.structuralEvents) ? state.structuralEvents.map((event) => ({ ...event })) : [],
    sweep: clonePrimaryRangeSweep(state.sweep),
    top: cloneFlowPoint(top),
    trend,
  };
}

function snapshotFromCandidateOfficialTrendState(candleCount, lastCandleAt, rawState, caStructure = null) {
  const state = {
    ...rawState,
    trend: candidateOfficialDeriveTrend(rawState),
  };
  const primaryRange = candidateOfficialPrimaryRange(state);
  const pivotLedger = primaryRange.pivotLedger ?? candidateOfficialBuildPivotLedger(state);
  const top = primaryRange.top;
  const bottom = primaryRange.bottom;
  const lastPivot = primaryRange.lastOfficialPivot ?? primaryRange.lastPivot;
  const activePivot = primaryRange.currentCandidate ?? primaryRange.activePivot;
  const movementAxis = primaryRange.movementAxis;
  const flow = {
    activePivot: cloneFlowPoint(activePivot),
    characterChange: cloneCharacterChange(primaryRange.characterChange),
    confirmationLevel: primaryRange.finalizationLevel ?? activePivot?.price ?? null,
    confirmationRule: primaryRange.finalizationRule,
    confirmedBottom: cloneFlowPoint(bottom),
    confirmedTop: cloneFlowPoint(top),
    lastConfirmedPivot: cloneFlowPoint(lastPivot),
    localFlow: {
      activePivot: cloneFlowPoint(state.localFlow?.activePivot),
      internalBottom: cloneFlowPoint(state.localFlow?.internalBottom),
      internalSwing: cloneFlowPoint(state.localFlow?.internalSwing),
      internalTop: cloneFlowPoint(state.localFlow?.internalTop),
    },
    movementAxis,
    nextExpectedPivot:
      movementAxis === 'seeking_top'
        ? ['HH', 'LH']
        : movementAxis === 'seeking_bottom'
          ? ['HL', 'LL']
          : null,
    pivotOptions:
      movementAxis === 'seeking_top'
        ? ['HH', 'LH']
        : movementAxis === 'seeking_bottom'
          ? ['HL', 'LL']
          : [],
    preconfirmedAt: null,
    preconfirmedPivot: null,
    preconfirmedReference: null,
    primaryRange,
    primaryStructure: {
      activePivot: cloneFlowPoint(activePivot),
      bottom: cloneFlowPoint(bottom),
      lastPivot: cloneFlowPoint(lastPivot),
      movementAxis,
      top: cloneFlowPoint(top),
    },
    reversalBias: 'none',
    reversalPivotStrength: 'none',
    reversalPivotStrengthReason: null,
    sequenceState: canonicalTrendSequenceState(primaryRange.mode),
    trend: primaryRange.trend,
    // Cripto Alert: estado da estrutura de extremos, para o modo incremental continuar de onde parou.
    caStructureState: cloneCaStructureState(caStructure),
  };
  // Cripto Alert: um HH/HL/LH/LL por tendência, sempre os extremos (ver ca-structure.mjs).
  const structure = buildCaStructure(caStructure, state);
  const pick = (mark, fallback) => (structure ? mark ?? fallback ?? null : fallback ?? null);
  const latestHH = pick(structure?.hh, primaryRange.confirmedHH);
  const latestHL = pick(structure?.hl, primaryRange.confirmedHL);
  const latestLH = pick(structure?.lh, primaryRange.confirmedLH);
  const latestLL = pick(structure?.ll, primaryRange.confirmedLL);
  const bullishStructure = structure?.trend === 'bullish';
  const referenceTop = structure ? (bullishStructure ? structure.hh : structure.lh ?? structure.hh) ?? top : top;
  const referenceBottom = structure ? (bullishStructure ? structure.hl ?? structure.ll : structure.ll) ?? bottom : bottom;
  const snapshot = {
    ...buildEmptySnapshot(candleCount, 'ready', lastCandleAt),
    candidatePivotPrice: activePivot ? roundPrice(activePivot.price) : null,
    candidatePivotType: activePivot?.type ?? null,
    candidateStartedAt: activePivot?.at ?? null,
    flow,
    pivotLedger,
    hhAt: latestHH?.at ?? null,
    hhPrice: latestHH ? roundPrice(latestHH.price) : null,
    hlAt: latestHL?.at ?? null,
    hlPrice: latestHL ? roundPrice(latestHL.price) : null,
    lastPivotAt: lastPivot?.at ?? null,
    lastPivotPrice: lastPivot ? roundPrice(lastPivot.price) : null,
    lastPivotType: lastPivot?.type ?? null,
    lhAt: latestLH?.at ?? null,
    lhPrice: latestLH ? roundPrice(latestLH.price) : null,
    llAt: latestLL?.at ?? null,
    llPrice: latestLL ? roundPrice(latestLL.price) : null,
    phase: canonicalTrendPhase(primaryRange.mode, primaryRange.trend),
    provisionalPivotAt: activePivot?.at ?? null,
    provisionalPivotConfirmation: primaryRange.finalizationRule,
    provisionalPivotPrice: activePivot ? roundPrice(activePivot.price) : null,
    provisionalPivotType: activePivot?.type ?? null,
    trend: primaryRange.trend,
    validHighAt: referenceTop?.at ?? null,
    validHighPrice: referenceTop ? roundPrice(referenceTop.price) : null,
    validHighType: referenceTop?.type ?? null,
    validLowAt: referenceBottom?.at ?? null,
    validLowPrice: referenceBottom ? roundPrice(referenceBottom.price) : null,
    validLowType: referenceBottom?.type ?? null,
    validStructureConfirmedAt: lastPivot?.confirmedAt ?? lastPivot?.at ?? null,
    validStructureTrend: primaryRange.trend,
    structure,
  };

  return snapshot;
}

function calculateMainInternalStructuralTrendStructureV9(inputCandles) {
  const candles = normalizeTrendStructureCandles(inputCandles);
  const candleCount = candles.length;
  const lastCandleAt = candleCount > 0 ? candles[candleCount - 1].endAt : null;

  if (candleCount === 0) {
    return {
      events: [],
      needsRebuild: false,
      snapshot: buildEmptySnapshot(0, 'no_data'),
    };
  }

  if (candleCount < TREND_STRUCTURE_MIN_READY_CANDLES) {
    return {
      events: [],
      needsRebuild: false,
      snapshot: buildEmptySnapshot(candleCount, 'insufficient_data', lastCandleAt),
    };
  }

  const events = [];
  const state = candidateOfficialEmptyState();
  state.recordPivotHistory = false;

  let caStructure = null;

  for (let index = 1; index < candles.length; index += 1) {
    candidateOfficialAdvanceState(state, candles[index - 1], candles[index], events);
    caStructure = advanceCaStructure(caStructure, candidateOfficialDeriveTrend(state), candles, index);
  }

  return {
    events,
    needsRebuild: false,
    snapshot: snapshotFromCandidateOfficialTrendState(candleCount, lastCandleAt, state, caStructure),
  };
}

export function calculateTrendStructureFromCandles(inputCandles) {
  return calculateMainInternalStructuralTrendStructureV9(inputCandles);
}
